import { i18n } from '../i18n.js';
import { getCloudAchievementBannerConsumer } from '../app-services.js';
import { MoonfallClickGuard } from '../utils/click-guard.js';
import { MoonfallCloudGuard } from '../utils/cloud-mutation-guard.js';
import { MoonfallCloud } from '../convex-client.js';
import { AssetPreloader } from './asset-preloader.js';
import { LoadScheduler } from '../utils/load-scheduler.js';
import { Cards } from './cards.js';
import { openListingShare } from './exchange-share.js';
import { openListingDetail } from './exchange-listing-detail.js';
import { displayedStardust } from './exchange-deep-link.js';
import { exchangeLock, exchangeLockMessage, playerFacingError } from './player-error.js';
import { CardTilt } from './card-tilt.js';
import { fetchPageAnswer } from './collection-page-answer.js';
import { HoverHandoff } from './hover-handoff.js';
import { createHoverIntent } from './hover-intent.js';
import { MoonExchangeData } from './moon-exchange-data.js';
import { SoundEffects } from './sound-effects.js';
import { Stardust } from './stardust.js';
import { StellarAssets } from './stellar-assets.js';
import { SurfaceEntryHold } from './surface-entry-hold.js';
import { TouchPreview } from './touch-preview.js';
import { uiPx, uiScaleOf } from './ui-scale.js';
import { DiscordPings } from './discord-pings.js';
let MoonExchange = {};

/**
 * How many listings a lane of this size shows.
 * @param {object} [lane]
 * @param {number} [lane.availableWidth]
 * @param {number} [lane.availableHeight]
 * @param {number} [lane.viewportWidth]
 * @param {number} [lane.viewportHeight]
 * @param {number} [lane.maxPageSize]
 * @returns {number}
 */
function calculateListingPageSize({
  availableWidth,
  availableHeight,
  viewportWidth,
  viewportHeight,
  maxPageSize = 24,
} = {}) {
  const maximum = Math.max(1, Math.floor(Number(maxPageSize) || 1));
  const width = Number(availableWidth);
  const height = Number(availableHeight);
  if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
    return maximum;
  }

  const screenWidth = Number(viewportWidth);
  const screenHeight = Number(viewportHeight);
  const isShortLandscape = Number.isFinite(screenWidth)
    && Number.isFinite(screenHeight)
    && screenWidth > screenHeight
    && screenHeight <= 560;
  const isCompactPortrait = !isShortLandscape && (
    Number.isFinite(screenWidth)
    && screenWidth <= 900
  );
  // The short-landscape renderer is intentionally a two-column transaction
  // table: each row preserves a full 44px touch target while two measured rows
  // keep four listings above the pager. Desktop keeps the historic one-column,
  // card-forward rows in the permanent two-pane workspace. The calculator
  // mirrors those real CSS tracks exactly so API pagination never creates an
  // internal scrolling lane.
  //
  // 46, not 44: a listing row is its 44px touch target plus the listing's own
  // 1px top and bottom borders. The old 44 was calibrated at 768x400 and
  // 812x375 — viewports at least 375 tall, which are not page boxes any phone
  // browser actually hands out. At the real iPhone SE landscape page box
  // (667x275) that under-count asked the lane for two rows it could not show,
  // and the second row's Buy control was clipped. Measure this against the
  // rendered row before changing it; `.moon-exchange-buy .btn` used to add a
  // further 8px of `.btn` margin on top.
  // These mirror stylesheet pixels, which the server serves scaled to the
  // screen (game/utils/uiScaleCss.js); above full HD they must grow with them,
  // or a 4K lane asks for twice the listings it can show.
  const scale = uiScaleOf(screenWidth, screenHeight);
  const gap = (isShortLandscape ? 4 : (isCompactPortrait ? 12 : 6)) * scale;
  const rowHeight = (isShortLandscape ? 46 : (isCompactPortrait ? 106 : 128)) * scale;
  const columns = isShortLandscape
    ? Math.min(2, Math.max(1, Math.floor((width + gap) / (280 * scale + gap))))
    : 1;
  const measuredRows = Math.max(1, Math.floor(
    (height + gap) / (rowHeight + gap)
  ));
  const rows = isShortLandscape ? Math.min(2, measuredRows) : measuredRows;
  return Math.min(maximum, columns * rows);
}

function calculatePreviewCardScale({
  previewWidth,
  previewHeight,
  cardWidth,
  cardHeight,
  inset = uiPx(2),
} = {}) {
  const availableWidth = Number(previewWidth) - (Math.max(0, Number(inset) || 0) * 2);
  const availableHeight = Number(previewHeight) - (Math.max(0, Number(inset) || 0) * 2);
  const naturalWidth = Number(cardWidth);
  const naturalHeight = Number(cardHeight);
  if (
    !(availableWidth > 0)
    || !(availableHeight > 0)
    || !(naturalWidth > 0)
    || !(naturalHeight > 0)
  ) return null;
  // Small desktop cards may be physically narrower than the dedicated sell
  // preview, so a bounded upscale keeps the preview from being a tiny card
  // island. That is only honest because the factor is consumed by --card-unit:
  // the card is built larger and rasters at that size. Handing the same factor
  // to a transform would stretch a layout-size raster instead.
  return Math.min(1.5, availableWidth / naturalWidth, availableHeight / naturalHeight);
}

function measureElementBox(element) {
  if (!element) return { width: 0, height: 0 };
  const computed = typeof window.getComputedStyle === 'function'
    ? window.getComputedStyle(element)
    : null;
  const bounds = element.getBoundingClientRect?.();
  return {
    width: Number(element.clientWidth)
      || Number(element.offsetWidth)
      || Number(bounds?.width)
      || Number.parseFloat(computed?.width)
      || 0,
    height: Number(element.clientHeight)
      || Number(element.offsetHeight)
      || Number(bounds?.height)
      || Number.parseFloat(computed?.height)
      || 0,
  };
}

/**
 * Return the card's box with no fit factor applied.
 *
 * Physical sizing means the measured box already contains the current factor.
 * Neutralising it during measurement keeps repeated ResizeObserver fits
 * idempotent instead of multiplying the scale on every callback.
 */
function measureNaturalCardBox(cardElement) {
  if (!cardElement?.style) return measureElementBox(cardElement);
  const previous = cardElement.style.getPropertyValue('--moon-exchange-card-scale');
  cardElement.style.setProperty('--moon-exchange-card-scale', '1');
  try {
    return measureElementBox(cardElement);
  } finally {
    if (previous) cardElement.style.setProperty('--moon-exchange-card-scale', previous);
    else cardElement.style.removeProperty('--moon-exchange-card-scale');
  }
}

function fitCardInsidePreview(cardElement, previewElement) {
  const preview = measureElementBox(previewElement);
  const card = measureNaturalCardBox(cardElement);
  const scale = calculatePreviewCardScale({
    previewWidth: preview.width,
    previewHeight: preview.height,
    cardWidth: card.width,
    cardHeight: card.height,
  });
  if (!(scale > 0)) return false;
  cardElement.style.setProperty('--moon-exchange-card-scale', String(scale));
  return true;
}


// A completed trade deserves a beat of reward. Restart-safe one-shot pop on the
// status line + a coin chime; kept pure (no closure state) so it is unit-tested
// directly and reused for every buy/sell/list success.
function celebrateExchangeStatus(statusEl, { play } = {}) {
  if (!statusEl) return;
  statusEl.classList.remove('moon-exchange-status--celebrate');
  void statusEl.offsetWidth; // force reflow so the animation replays on repeats
  statusEl.classList.add('moon-exchange-status--celebrate');
  // Reuse the existing achievement chime rather than a bespoke coin sound.
  if (typeof play === 'function') play('success');
}

MoonExchange = (function () {
  // Bounded atomic listings commit (docs/asset-loading.md law 2 + cohort rule).
  const EXCHANGE_REVEAL_CAP_MS = 2500;
  // The lane entrance (deck-builder.css me-listings-reveal, 0.3 s) is spent by
  // then even where no animationend arrives.
  const LANE_ENTRANCE_BACKSTOP_MS = 500;
  const FALLBACK_DEFAULT_UNIT_PRICE = 100;
  const instanceToken = Math.random().toString(36).slice(2);
  let allCards = [];
  let sellableCards = [];
  let ownedCardMap = new Map();
  let ownedCardStellarMap = new Map();
  let currentUserId = null;
  let stardust = 0;
  let maxListingQuantity = null;
  let maxUnitPrice = null;
  let unitPriceEdited = false;
  let feeRate = 0;
  /* The sell gate, as the server last reported it.
     `unlocked: null` means NOT HEARD YET, and it is a third state on purpose.
     The old default said `false` with `required: 0`, which read as unlocked to
     the check below — so the Create form painted first and the locked panel
     replaced it a moment later. Reported: "i see option to create listing and
     then it gets hiden after a bit". Starting locked instead would only mirror
     the flash onto everyone who IS unlocked, so until the server answers we
     show neither and the pane simply has nothing in it. */
  let sellUnlock = { unlocked: null, finished: 0, required: 0 };
  let listings = [];
  let nextCursor = null;
  let currentCursor = null;
  let cursorStack = [];
  let pageSize = 24;
  let activePageSize = pageSize;
  const DEFAULT_SORT = 'date_desc';
  const DEFAULT_OWNER_FILTER = 'all';
  const SORT_STORAGE_KEY = 'moonfall_exchange_sort';
  const VALID_SORTS = new Set(['date_desc', 'date_asc', 'price_asc', 'price_desc']);
  const VALID_OWNER_FILTERS = new Set(['all', 'mine', 'others']);
  const SEARCH_BACKFILL_POLL_INTERVAL_MS = 2500;
  const SEARCH_BACKFILL_POLL_MAX_INTERVAL_MS = 30000;
  const MAX_SEARCH_BACKFILL_POLL_ATTEMPTS = 24;
  let currentSort = DEFAULT_SORT;
  let currentOwnerFilter = DEFAULT_OWNER_FILTER;
  let searchText = '';
  let searchTimer = null;
  let isDone = true;
  let onStardustUpdate = null;
  let onInventoryUpdate = null;
  let isLoading = false;
  let pendingSelection = null;
  let searchBackfillStatus = null;
  let searchBackfillTimer = null;
  let searchBackfillAttempts = 0;
  let pendingRefresh = false;
  let pendingRefreshReset = false;
  let activeRequestKey = null;
  let pendingRefreshKey = null;
  let isLoadingSellableCards = false;
  let sellableLoadError = null;
  let listingsLoadError = null;
  let isOpen = false;
  /* Bounds the surface-open unlock drain (see open()). Generous on purpose:
     the thing it delivers is a once-per-account cardback, so a sale that lands
     inside the window is picked up by the next open or the next launch. */
  const UNLOCK_DRAIN_INTERVAL_MS = 60_000;
  let lastUnlockDrainAt = 0;
  let i18nListenersReady = false;
  let layoutResizeObserver = null;
  let layoutFrame = null;
  let viewportRenderSignature = '';
  let listingsRenderGeneration = 0;
  let sellPreviewGeneration = 0;
  let sellPreviewFitObserver = null;
  // The card the sell slot shows and what it was built from (renderSellPreview).
  let sellPreviewShown = null; // { cardEl, identity, card, cardData }
  /* A preview answers a pointer the player MOVED (hover-intent.js), as in the
     Collection's grid. Disarmed when the market comes up under a resting
     pointer: its tab pressed from the keyboard put the sell slot under the
     mouse, and its card opened 3.5x over the sell form the player had come to
     use (27 October 2026); and when a page of listings swaps in. */
  const hoverIntent = createHoverIntent({
    selector: '.moon-exchange-card-preview, .moon-exchange-sell-preview',
    within: () => getSection(),
  });
  // The card being put in the sell slot, until it is shown or given up.
  let sellPreviewReveal = null; // { generation, promise }
  // The card select's options as last written, so an unchanged list is kept.
  let renderedSellOptionsMarkup = '';
  // Where the sell column's list came from: the owned catalog, the server's
  // sellable list, or the card the column last showed (THE SELL COLUMN'S
  // LAST CARD, below).
  let sellListSource = null; // 'catalog' | 'server' | 'record' | null
  // Bumped when the dictionary or language changes, so a reconciling render
  // knows its printed words are out of date even when the listings are not.
  let localeRevision = 0;

  /* THE ENTRY PAGE, AHEAD OF THE TAB.
     The first listings page for the entry query (stored sort, all sellers, no
     search) is read by `warm()` — the Collection's open, intent on the tab —
     and again by `prepare()` the moment the tab is picked, so it no longer
     waits behind the Collection's catalog read. Every entry-shaped answer is
     remembered, so the next visit is presented from it.
     A page read at a larger size (the lane cannot be measured while hidden)
     is presented by slicing; its re-read brings the real cursor.
     AN ENTRY ADOPTS A PAGE YOUNGER THAN LISTINGS_WARM_FRESH_MS (read audit
     R10, 26 September 2026): the Collection's warm read the page and the
     entry read it again, whatever its age. An exact page read for this
     account within the window is the entry's answer, and nothing is read
     again — as the Store and the Decks tab adopt theirs. A sliced page is
     still re-read behind the view (for its cursor), and so is one read
     before this player's last trade (the refresh after the trade may not
     have landed). A page read for another account is never presented, and
     a read in flight for one is never joined: the answer is the viewer's own
     (`isMine`, the seller id).
     The owned catalog is NOT read ahead of intent (see readCatalog()). */
  const LISTINGS_WARM_FRESH_MS = 30_000;
  /* PAGES KEPT, AND ONE AHEAD (owner, 28 September 2026: "Moon Exchange
     loads pretty slowly ... maybe you could preload here one page or three").
     The market is every player's listings, so a page is only as good as its
     read: every page read is kept LISTINGS_WARM_FRESH_MS for the same query,
     viewer and trade revision (the entry page's rule), and once a page is on
     screen with its pictures in, the NEXT page is read and its rows' art
     warmed in the background (ROWS' ART AHEAD). Next and Prev within the window present the kept
     page with no read at all (a read still on its way is joined); past it,
     after this player's trade, for another query or another viewer, they
     read as before. Measured before (round 17 probe, same seat and account):
     Next read its page and downloaded its art at the press, 0.86 s on the
     cable desktop and 2.83 s on the WebKit 4G phone. */
  const PAGES_KEPT = 4;
  /** @type {Map<string, { owner: string | null, revision: number, at: number, settled: boolean, promise: Promise<any> }>} */
  const keptPages = new Map();
  let rememberedEntryPage = null; // { queryKey, limit, owner, tradeRevision, at, response }
  let entryPageRead = null; // { queryKey, limit, owner, promise, startedAt }
  // Every first-page read still on its way (the latest is entryPageRead).
  const entryPageReads = new Set();
  // Bumped when a trade by this player settles (a listing made, bought or
  // cancelled), whether or not its reply arrived.
  let tradeRevision = 0;
  let measuredPageSize = 0;
  // The Collection's catalog read (`/api/cards`), handed over by its warm or
  // its context; called only on intent on the tab.
  let cardsLoader = null;
  // Intent on the tab came before the Collection handed its loader over: the
  // hand-over reads the catalog. Dropped when the tab is picked or closed.
  let catalogIntentPending = false;
  // The owned catalog has answered this surface (intent's read, or the
  // Collection's hand-over before open()): the Collection memoises it, so the
  // tab's entry no longer waits on `/api/cards`.
  let catalogInHand = false;
  /* THE TAB OPENS BEFORE THE CATALOG (read audit F3, 28 September 2026).
     The Collection says so with its warm (`withoutCatalog`) and with its
     context (`catalogPending`, until the catalog lands). A row's face then
     takes its words from the page's own cards — the details route, for the
     rows the lane shows, in the player's language — kept per viewer and
     language (`pageCards`): a row's own `cardName`/`cardDescription` are the
     listing's English snapshot (measured on dev against the Japanese
     catalogue: 13 of 13 rows differ). The sell column, whose card list is
     the catalog, waits for it. */
  let facesReadAhead = false;
  let catalogPending = false;
  /* A COLLECTION TOO LARGE TO LIST WHOLE (card-scale, 21 October 2026): the
     Collection hands over no catalog, and the sell column finds its cards by
     name through the server, a page at a time (`/api/exchange/sellable`),
     instead of listing every card it holds. */
  let catalogComplete = true;
  let sellSearchTerm = '';
  let sellSearchTimer = null;
  let sellSearchGeneration = 0;
  let catalogLanded = null; // { promise, resolve } until the catalog lands
  // The sell column's error says the catalog failed (cleared when one lands).
  let sellErrorFromCatalog = false;
  let pageCards = { key: '', byId: new Map() };
  // The language the Collection's catalog was handed over in: it keeps its
  // catalog across a language change (A LANGUAGE CHANGE RE-DESCRIBES THE ROWS).
  let catalogLanguage = null;
  let entryHold = null;
  // Bumped by every open(): an entry a close (or a newer open) overtook stops.
  let openGeneration = 0;
  // When the tab was last pressed (prepare()): a page read since is fresh.
  let entryPressedAt = 0;

  function uiText(key, fallback, replacements = {}) {
    const translated = i18n?.t?.(key, replacements);
    const text = translated && translated !== key ? translated : fallback;
      /* BOTH brace styles, double FIRST.
         The copy uses `{{name}}` (as ui.json writes it) and, in a few
         places, single `{name}`. A helper that knows only one leaves the
         other on screen whenever i18n has not resolved. Order matters:
         substituting the single form first turns `{{count}}` into `{3}`,
         which is exactly how starter-reward.js was rendering it. */
      return Object.entries(replacements || {}).reduce(
          (result, [name, value]) => String(result)
              .split(`{{${name}}}`).join(String(value))
              .split(`{${name}}`).join(String(value)),
          String(text),
      );
  }

  const formatStardust = (value) => Stardust?.format?.(value) ?? String(value);

  function renderStardustText(element, key, fallback, value, replacement = 'amount') {
    element.dataset.countValue = String(value);
    element.textContent = uiText(key, fallback, { [replacement]: formatStardust(value) });
    element.title = uiText(key, fallback, { [replacement]: value });
  }

  function applyExchangeTranslations() {
    if (i18n?.applyTranslations) {
      i18n.applyTranslations();
    }
  }

  function isStaleInstance() {
    return MoonExchange?._instanceToken !== instanceToken;
  }

  function normalizeSort(value) {
    return VALID_SORTS.has(value) ? value : DEFAULT_SORT;
  }

  function normalizeOwnerFilter(value) {
    return VALID_OWNER_FILTERS.has(value) ? value : DEFAULT_OWNER_FILTER;
  }

  function loadStoredSort() {
    if (typeof localStorage === 'undefined') return DEFAULT_SORT;
    try {
      const stored = localStorage.getItem(SORT_STORAGE_KEY);
      return normalizeSort(stored || DEFAULT_SORT);
    } catch (err) {
      console.warn('[MoonExchange] Failed to load sort preference:', err);
      return DEFAULT_SORT;
    }
  }

  function persistSort(value) {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(SORT_STORAGE_KEY, value);
    } catch (err) {
      console.warn('[MoonExchange] Failed to save sort preference:', err);
    }
  }

  function getSection() {
    return document.getElementById('collection-exchange-section');
  }

  function ensureLayout() {
    const section = getSection();
    if (!section || section.dataset.exchangeReady === 'true') return;

    section.dataset.exchangeReady = 'true';
    section.innerHTML = `
      <div class="moon-exchange-grid" data-mobile-pane="market">
        <div class="moon-exchange-panel moon-exchange-sell">
          <div class="moon-exchange-panel-header">
            <h4 data-i18n="collection.exchange.createTitle">Create Sell Listing</h4>
          </div>
          <div class="moon-exchange-form" hidden>
            <label class="moon-exchange-label" for="moon-exchange-card-select" data-i18n="collection.exchange.card">Card</label>
            <input type="search" id="moon-exchange-card-search" class="deck-builder-search moon-exchange-input" autocomplete="off" hidden
              placeholder="Find a card to sell by name" aria-label="Find a card to sell by name"
              data-i18n-placeholder="collection.exchange.findCardToSell" data-i18n-aria-label="collection.exchange.findCardToSell" />
            <select id="moon-exchange-card-select" class="deck-filter"></select>
            <div class="moon-exchange-availability" id="moon-exchange-availability">Available: 0</div>
            <div class="moon-exchange-sell-row">
              <div class="moon-exchange-sell-preview" id="moon-exchange-sell-preview">
                <div class="moon-exchange-sell-placeholder" data-i18n="collection.exchange.selectPreview">Select a card to preview.</div>
              </div>
              <div class="moon-exchange-sell-fields">
                <div class="moon-exchange-field">
                  <label class="moon-exchange-label" for="moon-exchange-unit-price" data-i18n="collection.exchange.unitPrice">Unit price (Stardust)</label>
                  <input type="number" class="deck-builder-search moon-exchange-input" id="moon-exchange-unit-price" min="1" step="1" value="${FALLBACK_DEFAULT_UNIT_PRICE}" placeholder="${FALLBACK_DEFAULT_UNIT_PRICE}" />
                </div>
                <div class="moon-exchange-field">
                  <label class="moon-exchange-label" for="moon-exchange-quantity" data-i18n="collection.exchange.quantity">Quantity</label>
                  <input type="number" class="deck-builder-search moon-exchange-input" id="moon-exchange-quantity" min="1" step="1" placeholder="1" />
                </div>
              </div>
            </div>

            <div class="moon-exchange-proceeds" aria-live="polite">
              <div class="moon-exchange-proceeds-row">
                <span data-i18n="collection.exchange.grossListing">Gross listing</span>
                <strong id="moon-exchange-gross-value">0 Stardust</strong>
              </div>
              <div class="moon-exchange-fee-policy" id="moon-exchange-fee-policy">Fee: 0% per completed purchase</div>
            </div>

            <button class="btn deck-btn moon-exchange-create-btn" id="moon-exchange-create-btn" data-i18n="collection.exchange.createListing">Create Listing</button>
          </div>
          <div class="moon-exchange-sell-empty" id="moon-exchange-sell-empty" hidden>
            <div class="moon-exchange-sell-empty-icon" aria-hidden="true">🪙</div>
            <p class="moon-exchange-sell-empty-title" data-i18n="collection.exchange.noSellableCards">No eligible cards to sell yet</p>
            <p class="moon-exchange-sell-empty-hint" data-i18n="collection.exchange.noSellableCardsHint">Eligible cards will appear here.</p>
          </div>
          <!-- NO data-i18n ON THE TWO INTERPOLATING LINES. Both now carry a
               required-count placeholder, and applyTranslations() re-applies a
               data-i18n element's raw string with no replacements, which would
               print the placeholder verbatim at the player. They are filled by
               applySellUnlockState, which has the number. (No backticks in
               this comment: it lives inside a template literal.) -->
          <div class="moon-exchange-sell-locked" id="moon-exchange-sell-locked" hidden>
            <div class="moon-exchange-sell-empty-icon" aria-hidden="true">🌘</div>
            <p class="moon-exchange-sell-empty-title" id="moon-exchange-sell-locked-title"></p>
            <p class="moon-exchange-sell-empty-hint" id="moon-exchange-sell-locked-progress"></p>
            <p class="moon-exchange-sell-empty-hint" id="moon-exchange-sell-locked-body"></p>
          </div>
          <div class="moon-exchange-sell-error" id="moon-exchange-sell-error" role="alert" hidden>
            <p data-i18n="collection.exchange.loadSellableFailed">Sell inventory is unavailable.</p>
            <button type="button" class="btn deck-btn" id="moon-exchange-sell-retry" data-i18n="collection.exchange.retry">Retry</button>
          </div>
          <div class="moon-exchange-status" id="moon-exchange-status" role="status" aria-live="polite"></div>
        </div>

        <div class="moon-exchange-panel moon-exchange-market">
          <div class="moon-exchange-panel-header">
            <h4 id="moon-exchange-listings-title" data-i18n="collection.exchange.openListings">Open Listings</h4>
          </div>
          <div class="moon-exchange-command-bar moon-exchange-market-controls">
            <div class="moon-exchange-view-switcher" role="group" aria-label="Moon Exchange view" data-i18n-aria-label="collection.exchange.workspaceView">
              <button type="button" class="btn deck-btn moon-exchange-view-btn is-active" id="moon-exchange-view-market" aria-pressed="true" data-pane="market" data-i18n="collection.exchange.openListings">Open Listings</button>
              <button type="button" class="btn deck-btn moon-exchange-view-btn" id="moon-exchange-view-sell" aria-pressed="false" data-pane="sell" data-i18n="collection.exchange.createTitle">Create Sell Listing</button>
            </div>
            <div class="moon-exchange-summary" id="moon-exchange-summary" role="status" aria-live="polite">Showing 0 on this page</div>
            <input type="text" id="moon-exchange-search" class="deck-builder-search" placeholder="Search by name or description" aria-label="Search listings" data-i18n-placeholder="collection.exchange.searchPlaceholder" data-i18n-aria-label="collection.exchange.searchLabel" />
            <button type="button" class="btn deck-btn moon-exchange-filters-toggle" id="moon-exchange-filters-toggle" aria-expanded="false" aria-controls="moon-exchange-filter-menu" data-i18n="collection.filters.mobileToggle">Filters</button>
            <div class="moon-exchange-filter-menu" id="moon-exchange-filter-menu" hidden>
              <select id="moon-exchange-owner-filter" class="deck-filter" aria-label="Listing account filter" data-i18n-aria-label="collection.exchange.ownerFilter">
                <option value="all" selected data-i18n="collection.exchange.filters.all">All listings</option>
                <option value="mine" data-i18n="collection.exchange.filters.mine">My listings</option>
                <option value="others" data-i18n="collection.exchange.filters.others">Others</option>
              </select>
              <select id="moon-exchange-sort" class="deck-filter" aria-label="Sort listings" data-i18n-aria-label="collection.exchange.sortLabel">
                <option value="date_desc" selected data-i18n="collection.exchange.sort.newest">Newest</option>
                <option value="date_asc" data-i18n="collection.exchange.sort.oldest">Oldest</option>
                <option value="price_asc" data-i18n="collection.exchange.sort.stardustAsc">Stardust ↑</option>
                <option value="price_desc" data-i18n="collection.exchange.sort.stardustDesc">Stardust ↓</option>
              </select>
            </div>
            <button class="btn deck-btn moon-exchange-refresh" id="moon-exchange-refresh" data-i18n="collection.exchange.refresh">Refresh</button>
          </div>
          <div class="moon-exchange-listings" id="moon-exchange-listings" aria-busy="false" aria-labelledby="moon-exchange-listings-title"></div>
          <div class="moon-exchange-pagination">
            <button class="btn deck-btn" id="moon-exchange-prev" disabled data-i18n="common.prev">Prev</button>
            <div class="moon-exchange-page-info" id="moon-exchange-page-info" data-i18n="common.pageOne" data-i18n-runtime="true">Page 1</div>
            <button class="btn deck-btn" id="moon-exchange-next" disabled data-i18n="common.next">Next</button>
          </div>
        </div>
      </div>
    `;
    applyExchangeTranslations();
  }

  function setStatus(message, tone = 'info') {
    const statusEl = document.getElementById('moon-exchange-status');
    if (!statusEl) return;
    statusEl.textContent = message || '';
    statusEl.classList.remove('success', 'error');
    if (tone === 'success') {
      statusEl.classList.add('success');
      celebrateExchangeStatus(statusEl, { play: (id) => SoundEffects?.play?.(id) });
    } else {
      statusEl.classList.remove('moon-exchange-status--celebrate');
    }
    if (tone === 'error') statusEl.classList.add('error');
  }

  function setMobilePane(pane = 'market') {
    const nextPane = pane === 'sell' ? 'sell' : 'market';
    const grid = document.querySelector('.moon-exchange-grid');
    if (grid) grid.dataset.mobilePane = nextPane;
    setFiltersOpen(false);
    document.querySelectorAll('.moon-exchange-view-btn').forEach((button) => {
      const active = button.dataset.pane === nextPane;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });

    /* THE GATE HAS TO SURVIVE A PANE SWITCH. `applySellUnlockState` ran once,
       at config load, when a phone lays the sell pane out at zero size. Bringing
       that pane on screen re-lays it out — and without this the Create form
       came back for an account the server will always refuse, which is the gate
       showing a control it does not honour. Re-assert it here, where the pane
       becomes real. */
    applySellUnlockState();

    /* A phone shows ONE pane at a time, so the sell form is laid out at zero
       size until this switch. The preview card cannot settle against a box with
       no dimensions, so the card the form opens on stayed in its hidden staging
       state and the player saw an empty slot — while picking any other card
       re-rendered into a pane that was visible by then, which is why previews
       appeared "if i select a different card but not for the starting card".
       Re-render for the current selection once the pane is actually on screen.
       A card that did settle there is kept: the render reconciles. */
    if (nextPane === 'sell') {
      const select = document.getElementById('moon-exchange-card-select');
      const selected = parseCardKey(select?.value);
      if (selected.cardId) renderSellPreview(selected.cardId, selected.variant);
    }
  }

  function isCompactExchangeLayout() {
    const width = Number(window.innerWidth) || 0;
    const height = Number(window.innerHeight) || 0;
    return (width > height && height <= 560)
      || (width <= height && width <= 768);
  }

  function syncCommandBarPlacement() {
    const section = getSection();
    const grid = section?.querySelector('.moon-exchange-grid');
    const market = grid?.querySelector('.moon-exchange-market');
    const header = market?.querySelector('.moon-exchange-panel-header');
    const commandBar = section?.querySelector('.moon-exchange-command-bar');
    if (!section || !grid || !market || !header || !commandBar) return;

    if (isCompactExchangeLayout()) {
      if (commandBar.parentElement !== section) {
        section.insertBefore(commandBar, grid);
      }
    } else if (commandBar.parentElement !== market) {
      header.insertAdjacentElement('afterend', commandBar);
    }

    const menu = document.getElementById('moon-exchange-filter-menu');
    const toggle = document.getElementById('moon-exchange-filters-toggle');
    if (!isCompactExchangeLayout()) {
      if (menu) menu.hidden = false;
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    } else if (menu && toggle?.getAttribute('aria-expanded') !== 'true') {
      menu.hidden = true;
    }
  }

  function setFiltersOpen(open) {
    const toggle = document.getElementById('moon-exchange-filters-toggle');
    const menu = document.getElementById('moon-exchange-filter-menu');
    if (!isCompactExchangeLayout()) {
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      if (menu) menu.hidden = false;
      return;
    }
    const expanded = open === true;
    if (toggle) toggle.setAttribute('aria-expanded', String(expanded));
    if (menu) menu.hidden = !expanded;
  }

  function updateSellerEstimate() {
    const grossEl = document.getElementById('moon-exchange-gross-value');
    const feeEl = document.getElementById('moon-exchange-fee-policy');
    if (!grossEl || !feeEl) return;
    const price = Number(document.getElementById('moon-exchange-unit-price')?.value || 0);
    const quantity = Number(document.getElementById('moon-exchange-quantity')?.value || 0);
    const gross = Number.isFinite(price) && price > 0 && Number.isFinite(quantity) && quantity > 0
      ? Math.floor(price) * Math.floor(quantity)
      : 0;
    const ratePercent = Math.round(Math.max(0, feeRate) * 1000) / 10;
    renderStardustText(
      grossEl,
      'collection.exchange.stardustAmount',
      '{{amount}} Stardust',
      gross,
    );
    feeEl.textContent = uiText(
      'collection.exchange.feePolicy',
      'Fee: {{rate}}% per completed purchase',
      { rate: ratePercent }
    );
  }

  /**
   * Show the gate rather than the form when the account has not earned the
   * Exchange yet.
   *
   * The tab, the market and every listing stay exactly where they were — only
   * the CREATE form is swapped for an explanation. A gate that removed the
   * feature would have deleted it instead of warming it up.
   */
  function applySellUnlockState() {
    const locked = document.getElementById('moon-exchange-sell-locked');
    const form = document.querySelector('.moon-exchange-form');
    if (!locked) return;

    /* Three states, not two. Unheard hides BOTH, so nothing has to be taken
       back once the answer arrives. `required` is no longer part of the test:
       the server resolves it through a config read that fails closed to the
       default and can never answer zero, so a zero here only ever meant "not
       heard", which is now said directly. */
    const heard = sellUnlock.unlocked !== null;
    const showLocked = heard && sellUnlock.unlocked !== true;
    locked.hidden = !showLocked;
    if (form) form.hidden = !heard || showLocked;

    if (!showLocked) return;
    const terms = { finished: sellUnlock.finished, required: sellUnlock.required };
    const fill = (id, key, fallback) => {
      const el = document.getElementById(id);
      if (el) el.textContent = uiText(key, fallback, terms);
    };
    /* WINS, SAID OUT LOUD. The gate counts victories (owner, 2026-08-25), so
       the copy must not say "matches" and leave a losing player wondering why
       the counter did not move. */
    fill('moon-exchange-sell-locked-title', 'collection.exchange.lockedTitle',
      'Opens after {{required}} wins');
    fill('moon-exchange-sell-locked-progress', 'collection.exchange.lockedProgress',
      '{{finished}} / {{required}} wins');
    fill('moon-exchange-sell-locked-body', 'collection.exchange.lockedBody',
      'Win {{required}} matches and the Exchange opens.');
  }

  function getNotEnoughStardustMessage() {
    return uiText('collection.exchange.notEnoughStardust', 'Not enough Stardust.');
  }

  /**
   * The sell gate refuses with a ConvexError carrying its progress, so the
   * message can say how far along the player is rather than only that they
   * are stopped. A plain throw would arrive from production as "Server Error".
   */
  /* Both of these now live in `player-error.js`: the listing panel needs the
     same two answers, and worded differently on the two doors they would be the
     divergence this screen exists to prevent. */
  const readExchangeLockedError = exchangeLock;
  const exchangeLockedMessage = exchangeLockMessage;

  function escapeHtml(text) {
    return String(text ?? '').replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[char]));
  }

  function normalizeExchangeImageSource(rawSource) {
    if (typeof rawSource !== 'string') return '';
    const source = rawSource.trim();
    if (!source) return '';
    try {
      const url = new URL(source, window.location?.href || 'http://localhost/');
      if (!['http:', 'https:', 'data:', 'blob:'].includes(url.protocol)) return '';
      let pathname = url.pathname;
      for (let count = 0; count < 4; count += 1) {
        const decoded = decodeURIComponent(pathname);
        if (decoded === pathname) break;
        pathname = decoded;
      }
      if (pathname === '/assets/art_placeholder.webp') return '';
      return source;
    } catch (_) {
      return '';
    }
  }

  function loadExchangeExactImage(image, rawSource, profile = {}) {
    if (!image) return false;
    Cards?.disposeCardArtImage?.(image);
    window.MoonfallRetryingImage?.dispose?.(image, { clearSource: true });
    delete image.dataset.exchangeExactSource;
    const source = normalizeExchangeImageSource(rawSource);
    if (!source) {
      image.removeAttribute('src');
      image.classList.add('card-art-unavailable');
      return false;
    }

    image.dataset.exchangeArtOwner = 'true';
    image.dataset.exchangeExactSource = source;
    const cards = Cards;
    if (typeof cards?.loadCardArtImage === 'function') {
      void cards.loadCardArtImage(image, source, profile, { directArtOnly: true });
      return true;
    }
    if (typeof window.MoonfallRetryingImage?.load === 'function') {
      window.MoonfallRetryingImage.load(image, source);
      return true;
    }

    image.setAttribute('src', source);
    if (typeof cards?.enhanceCardArt === 'function') {
      void cards.enhanceCardArt(image, source, profile);
    }
    return false;
  }

  /** Dispose the exact images whose art never settled; true when any was. */
  function disposeUnsettledExchangeImages(root) {
    let disposed = false;
    root?.querySelectorAll?.(
      'img[data-exchange-exact-source], img[data-exchange-art-owner]',
    ).forEach((image) => {
      if (
        image.closest('.moon-exchange-paint-stage') === null
        && image.getAttribute('src')
        && image.complete
        && image.naturalWidth > 0
        && !image.classList.contains('card-art-loading')
      ) return;
      Cards?.disposeCardArtImage?.(image);
      window.MoonfallRetryingImage?.dispose?.(image);
      disposed = true;
    });
    return disposed;
  }

  function disposeExchangeExactImages(root) {
    root?.querySelectorAll?.(
      'img[data-exchange-exact-source], img[data-exchange-art-owner]',
    ).forEach((image) => {
      Cards?.disposeCardArtImage?.(image);
      window.MoonfallRetryingImage?.dispose?.(image);
    });
  }

  function buildCardKey(cardId, variant) {
    return `${cardId}::${variant}`;
  }

  function parseCardKey(value) {
    const raw = String(value || '');
    if (!raw) return { cardId: '', variant: 'normal' };
    const parts = raw.split('::');
    const cardId = parts[0] || '';
    const variant = parts[1] === 'stellar' ? 'stellar' : 'normal';
    return { cardId, variant };
  }

  function getOwnedQty(cardId, variant) {
    const sellable = sellableCards.find((entry) => entry.cardId === cardId && entry.variant === variant);
    if (sellable) return Number(sellable.available || 0);
    if (!cardId) return 0;
    const map = variant === 'stellar' ? ownedCardStellarMap : ownedCardMap;
    return Number(map.get(cardId) || 0);
  }

  function getStellarAsset(cardId) {
    if (!StellarAssets?.get) return null;
    return StellarAssets.get(cardId);
  }

  function buildCardElementData(card, variant = 'normal', stellarAsset = null) {
    const isStellar = variant === 'stellar';
    const data = {
      id: card.id || card.cardId,
      instanceId: card.id || card.cardId,
      name: card.baseName || card.name,
      type: card.type || 'Character',
      manaCost: card.cost || 0,
      picture: card.image || card.picture || card.id,
      imageUrl: card.image || card.picture,
      description: card.description || '',
      targetingType: card.targetingType || 'None',
      character: card.character,
      isStellar,
    };

    if (isStellar && stellarAsset?.status === 'complete') {
      StellarAssets?.applyMediaToCard?.(data, stellarAsset);
    }

    return data;
  }

  /**
   * The sell preview's slot is sized by the composer's rows, so it changes with
   * the page box — a rotation, or an iOS URL bar collapsing mid-scroll. The fit
   * was computed once at render time and never revisited, which left the card
   * either overflowing its slot (and clipped) or shrunken inside it. Re-settle
   * on an actual box change; the observer dies with the slot it watches.
   */
  function keepCardFittedToPreview(cardElement, cardData, previewElement) {
    sellPreviewFitObserver?.disconnect();
    sellPreviewFitObserver = null;
    if (typeof window.ResizeObserver !== 'function') return;
    const generation = sellPreviewGeneration;
    let pending = false;
    sellPreviewFitObserver = new window.ResizeObserver(() => {
      if (generation !== sellPreviewGeneration
        || !previewElement.isConnected
        || !cardElement.isConnected) {
        sellPreviewFitObserver?.disconnect();
        sellPreviewFitObserver = null;
        return;
      }
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => {
        pending = false;
        if (generation !== sellPreviewGeneration || !cardElement.isConnected) return;
        void settleExchangeCard(cardElement, cardData, previewElement);
      });
    });
    sellPreviewFitObserver.observe(previewElement);
  }

  async function settleExchangeCard(cardElement, cardData, previewElement, sourceCard = null) {
    if (!fitCardInsidePreview(cardElement, previewElement)) return false;
    const settle = Cards?.settleCardRasterForPresentation;
    if (typeof settle !== 'function') return false;
    try {
      const options = {
        oversample: 1,
        stableGeometry: true,
      };
      if (sourceCard) options.sourceCard = sourceCard;
      return await settle(cardElement, cardData, options) === true;
    } catch (_) {
      return false;
    }
  }

  function hydrateStellarMediaInPlace(cardElement, card, stellarAsset) {
    if (
      !cardElement
      || !card
      || stellarAsset?.status !== 'complete'
      || cardElement.dataset.stellarMediaHydrated === 'true'
    ) return false;
    const imageElement = cardElement.querySelector('.card-image');
    if (!imageElement || typeof Cards?.attachStellarVideo !== 'function') return false;

    const cardData = buildCardElementData(card, 'stellar', stellarAsset);
    Cards.attachStellarVideo(cardElement, imageElement, cardData);
    Cards.autoPlayStellarVideo?.(cardElement);
    cardElement.dataset.stellarMediaHydrated = 'true';
    return true;
  }

  function applyInventoryDelta(cardId, variant, delta) {
    if (!cardId || !Number.isFinite(delta)) return;
    const map = variant === 'stellar' ? ownedCardStellarMap : ownedCardMap;
    const current = Number(map.get(cardId) || 0);
    const nextQty = current + delta;
    if (nextQty > 0) {
      map.set(cardId, nextQty);
    } else {
      map.delete(cardId);
    }
    // The remembered card, shown while the catalog is on its way, follows
    // the trade the server just accepted.
    if (sellListSource === 'record') {
      sellableCards = sellableCards
        .map((entry) => (entry.cardId === cardId && entry.variant === variant
          ? { ...entry, available: Number(entry.available || 0) + delta }
          : entry))
        .filter((entry) => Number(entry.available) > 0);
    }
    if (typeof onInventoryUpdate === 'function') {
      onInventoryUpdate(cardId, nextQty, variant);
    }
  }

  /* Bounded: ~1s of animation frames. The slot appears on the very next paint
     in practice; the cap only stops a closed panel from retrying forever. */
  const SELL_PREVIEW_MOUNT_ATTEMPTS = 60;

  function retrySellPreviewWhenMounted(generation, cardId, variant, attempt = 0) {
    if (generation !== sellPreviewGeneration) return;
    if (attempt >= SELL_PREVIEW_MOUNT_ATTEMPTS) return;
    const schedule = window.requestAnimationFrame
      ? window.requestAnimationFrame.bind(window)
      : (fn) => setTimeout(fn, 16);
    schedule(() => {
      if (generation !== sellPreviewGeneration || !isOpen || isStaleInstance()) return;
      if (!document.getElementById('moon-exchange-sell-preview')) {
        retrySellPreviewWhenMounted(generation, cardId, variant, attempt + 1);
        return;
      }
      // The slot exists now; render for whatever is CURRENTLY selected.
      const selected = parseCardKey(document.getElementById('moon-exchange-card-select')?.value);
      renderSellPreview(selected.cardId || cardId, selected.variant || variant);
    });
  }

  function selectedSellKey() {
    const select = /** @type {HTMLSelectElement|null} */ (
      document.getElementById('moon-exchange-card-select')
    );
    return select?.value || '';
  }

  /**
   * What a sell preview card stands for: the card, its print and the language
   * it was printed in. Nothing else, on purpose. The same card arrives from
   * two sources — the Collection's catalog and the server's sellable list,
   * which resolves its own image URL — and a purchase swaps one for the other
   * while the player changes nothing.
   */
  function sellPreviewIdentity(cardId, variant) {
    return `${localeRevision}|${buildCardKey(cardId, variant)}`;
  }

  /** A line in the sell slot in place of a card ('' leaves the well empty). */
  function showSellPreviewMessage(previewSlot, text) {
    sellPreviewShown = null;
    disarmListingHoverPreview(previewSlot);
    let placeholder = previewSlot.querySelector(':scope > .moon-exchange-sell-placeholder');
    Array.from(previewSlot.childNodes).forEach((child) => {
      if (child !== placeholder) child.remove();
    });
    if (!placeholder) {
      placeholder = document.createElement('div');
      placeholder.className = 'moon-exchange-sell-placeholder';
      previewSlot.appendChild(placeholder);
    }
    // This module writes the line from here on, in the current language on
    // every render; a later applyTranslations() must not put the prompt back.
    placeholder.removeAttribute('data-i18n');
    if (placeholder.textContent !== text) placeholder.textContent = text;
  }

  /* Optional Stellar media may arrive after the immutable static art. Attach
     it to the existing card node so a settled first paint is never replaced
     or restarted merely because video became available. A render that keeps
     the card picks up a load an earlier render's generation abandoned. */
  async function hydrateSellPreviewMedia(shown, cardId, generation) {
    if (!shown.mediaPending || !StellarAssets?.load) return;
    try {
      await StellarAssets.load([cardId]);
    } catch (_) {
      return;
    }
    const loadedAsset = getStellarAsset(cardId);
    const selected = parseCardKey(selectedSellKey());
    if (
      !loadedAsset
      || generation !== sellPreviewGeneration
      || sellPreviewShown !== shown
      || !shown.cardEl.isConnected
      || selected.cardId !== cardId
      || selected.variant !== 'stellar'
    ) return;
    if (hydrateStellarMediaInPlace(shown.cardEl, shown.card, loadedAsset)) {
      shown.mediaPending = false;
    }
  }

  /** Whether the sell column is laid out: a phone's market pane, or a locked sell form, hides it. */
  function sellColumnShown() {
    const box = document.getElementById('moon-exchange-sell-preview')?.getBoundingClientRect?.();
    return Boolean(box && box.width > 0 && box.height > 0);
  }

  /* THE SELL COLUMN'S CARD IS PART OF THE TAB'S FIRST FRAME (round 17, 28
     September 2026). With the entry rows' art read ahead, the tab's hold let
     go before the sell column's first card had settled, and that card popped
     in 64-271 ms after the rest (the meter's collection journey, Chrome
     desktop, an account with cards to sell). The open waits for it too,
     never past the reveal cap, and not at all while the sell column is not
     laid out (a phone's market pane). And the card is fitted to the box it
     is shown in: it was fitted when it was built, before the market rows
     were in, and its observer refitted it a frame after the reveal (a
     return to the tab, Chrome desktop: 13 morphs, the card 1% smaller). */
  function sellPreviewSettled() {
    const slot = document.getElementById('moon-exchange-sell-preview');
    if (!slot || !sellColumnShown()) return Promise.resolve();
    const reveal = sellPreviewReveal;
    const settling = reveal && reveal.generation === sellPreviewGeneration
      ? Promise.race([
        reveal.promise,
        new Promise((resolve) => {
          setTimeout(resolve, EXCHANGE_REVEAL_CAP_MS);
        }),
      ])
      : Promise.resolve();
    return settling.then(() => {
      const shown = sellPreviewShown;
      if (shown?.cardEl.parentElement === slot && slot.isConnected) fitCardInsidePreview(shown.cardEl, slot);
    });
  }

  function renderSellPreview(cardId, variant) {
    const generation = ++sellPreviewGeneration;
    sellPreviewFitObserver?.disconnect();
    sellPreviewFitObserver = null;
    // A closed tab lays no slot out, so a card built for it could never
    // settle (a context sync at the Collection's open built one anyway, and
    // left it staged): the open renders the column's card.
    if (!isOpen) return;
    const previewSlot = document.getElementById('moon-exchange-sell-preview');
    if (!previewSlot) {
      /* The FIRST render runs before the sell panel is mounted, so this used to
         return silently and the card the form opens on never got a preview —
         while picking any other card re-rendered into a slot that existed by
         then. Reported as: previews work "if i select a different card but not
         for the starting card". Wait for the slot instead of dropping the
         render; a newer selection supersedes this one through `generation`. */
      if (cardId) retrySellPreviewWhenMounted(generation, cardId, variant);
      return;
    }

    /* RECONCILE, NEVER REBUILD. Every re-render of the sell column ends here —
       a context sync, a Stardust or inventory announcement, a purchase, a new
       or cancelled listing, a return to the tab, the phone's pane switch — and
       this used to swap the settled card for an empty slot reading "Select a
       card to preview." plus a hidden copy of the same card, shown a frame or
       more later: the card blinked and a prompt that was not true flashed.
       The slot is kept now. Its card stays while it is still the selection; a
       different card is staged hidden beside it and replaces it in the task
       it settles; the prompt is written only when no card is chosen. A
       staging copy from a render this one supersedes is dropped. */
    previewSlot.querySelectorAll(':scope > .moon-exchange-preview-staging')
      .forEach((staged) => staged.remove());
    const shown = sellPreviewShown?.cardEl.parentElement === previewSlot ? sellPreviewShown : null;
    if (!shown) sellPreviewShown = null;

    if (!cardId) {
      // While the card list is on its way the well stays empty: no card can be chosen yet.
      showSellPreviewMessage(previewSlot, catalogPending
        ? ''
        : uiText('collection.exchange.selectPreview', 'Select a card to preview.'));
      return;
    }

    const card = sellableCards.find((entry) => entry.cardId === cardId && entry.variant === variant)
      || allCards.find((entry) => entry.id === cardId);
    if (!card || !Cards?.createCardElement) {
      showSellPreviewMessage(previewSlot, uiText('collection.exchange.previewUnavailable', 'Card preview unavailable.'));
      return;
    }

    const identity = sellPreviewIdentity(cardId, variant);
    if (shown?.identity === identity) {
      // Already on screen. At most re-fit it: the observer does, on a box change.
      keepCardFittedToPreview(shown.cardEl, shown.cardData, previewSlot);
      void hydrateSellPreviewMedia(shown, cardId, generation);
      return;
    }

    const stellarAsset = variant === 'stellar' ? getStellarAsset(cardId) : null;
    const cardData = buildCardElementData(card, variant, stellarAsset);
    const cardEl = Cards.createCardElement(
      cardData,
      `moon-exchange-sell-${card.id || card.cardId}-${variant}`
    );
    cardEl.classList.add('deck-builder-card', 'moon-exchange-preview-staging');
    cardEl.setAttribute('aria-hidden', 'true');
    if (variant === 'stellar') {
      cardEl.dataset.stellarPreview = 'collection';
    }
    // With no card on screen the well stays, empty: it does not ask for a
    // card while one is on its way.
    if (!shown) showSellPreviewMessage(previewSlot, '');
    previewSlot.appendChild(cardEl);

    // Settled when this card is shown, or given up (sellPreviewSettled).
    let revealed = () => {};
    sellPreviewReveal = {
      generation,
      promise: new Promise((resolveReveal) => { revealed = () => resolveReveal(undefined); }),
    };
    void (async () => {
      try {
        await revealSellPreview();
      } finally {
        revealed();
      }
    })();

    async function revealSellPreview() {
      const cardKey = buildCardKey(cardId, variant);
      const sourceCard = Array.from(document.querySelectorAll(
        '#moon-exchange-listings .moon-exchange-card-preview'
      )).find((element) => element.dataset.cardKey === cardKey)
        ?.querySelector(':scope > .card') || null;
      const settled = await settleExchangeCard(cardEl, cardData, previewSlot, sourceCard);
      const selected = parseCardKey(selectedSellKey());
      if (
        generation !== sellPreviewGeneration
        || !isOpen
        || isStaleInstance()
        || document.getElementById('moon-exchange-sell-preview') !== previewSlot
        || cardEl.parentElement !== previewSlot
        || selected.cardId !== cardId
        || selected.variant !== variant
      ) return;
      if (!settled) {
        showSellPreviewMessage(previewSlot, uiText('collection.exchange.previewUnavailable', 'Card preview unavailable.'));
        return;
      }

      // ONE STEP: whatever the slot showed leaves in the task that reveals
      // the new card, so there is never a frame with neither, or both.
      Array.from(previewSlot.childNodes).forEach((child) => {
        if (child !== cardEl) child.remove();
      });
      cardEl.classList.remove('moon-exchange-preview-staging');
      cardEl.removeAttribute('aria-hidden');
      const nextShown = {
        cardEl,
        identity,
        card,
        cardData,
        mediaPending: variant === 'stellar' && !stellarAsset,
      };
      sellPreviewShown = nextShown;
      keepCardFittedToPreview(cardEl, cardData, previewSlot);
      attachListingHoverPreview(previewSlot, card, variant);
      // Shown: the tab's first frame waits no longer (its media may follow).
      revealed();
      await hydrateSellPreviewMedia(nextShown, cardId, generation);
    }
  }

  // What each wrapper's hover enlarges; `null` enlarges nothing.
  const listingHoverTargets = new WeakMap();

  /** A wrapper that shows no card any more enlarges none. */
  function disarmListingHoverPreview(cardWrapper) {
    if (listingHoverTargets.has(cardWrapper)) listingHoverTargets.set(cardWrapper, null);
    delete cardWrapper.dataset.cardKey;
  }

  /**
   * @param {HTMLElement} cardWrapper the cell whose hover grows the card
   * @param {object} card
   * @param {'normal'|'stellar'} variant
   * @param {object} [options]
   * @param {() => void} [options.onActivate] what a click on the ENLARGED card
   *   does, once it has been put down. Only the listing rows pass one -- the
   *   sell panel's preview has no listing behind it, and stays inspect-only.
   */
  function attachListingHoverPreview(cardWrapper, card, variant, { onActivate } = {}) {
    if (!cardWrapper || !card || !Cards?.createCardElement) return;
    cardWrapper.dataset.cardKey = buildCardKey(card.id || card.cardId, variant);
    /* ONE BINDING PER WRAPPER. The sell slot is kept while the card in it
       changes (renderSellPreview reconciles), so attaching again re-aims the
       listeners it already has: a second pair would also enlarge a card the
       slot no longer shows. */
    const bound = listingHoverTargets.has(cardWrapper);
    listingHoverTargets.set(cardWrapper, { card, variant, onActivate });
    if (bound) return;
    let hoverActive = false;
    let hoverGeneration = 0;
    // The hover a neighbour's preview handed to this card, while it builds.
    let handoffEnter = null;

    // Touch: tap enlarges, tap again acts. The guard above already admits the
    // synthetic (untrusted) mouseenter this dispatches while rejecting the
    // compatibility one a real tap produces.
    TouchPreview?.attach?.(cardWrapper);
    const presentHoverPreview = async ({ card, variant, onActivate }) => {
      const cardId = card.id || card.cardId;
      const cardKey = buildCardKey(cardId, variant);
      hoverActive = true;
      const generation = ++hoverGeneration;
      if (HoverHandoff?.hasHandedOff?.()) {
        /* A neighbour's preview handed this card the hover; it stays until
           this one is revealed. Only a preview still in the pointer's hands
           goes (the shared clear is a tear-down: it lets the handed-off one
           go too, and the rows would show through until this one is up). */
        putDownPreview(currentHoverPreview());
      } else if (Cards?.clearHoverPreview) {
        Cards.clearHoverPreview();
      }

      const sourceCard = cardWrapper.querySelector(':scope > .card');
      const prepareRaster = Cards.prepareCardRasterForInteraction;
      if (typeof prepareRaster === 'function') {
        let rasterReady = false;
        try {
          rasterReady = await prepareRaster(sourceCard, 'hover');
        } catch (_) {
          rasterReady = false;
        }
        if (rasterReady !== true) return;
      }
      if (
        !hoverActive
        || generation !== hoverGeneration
        || !isOpen
        || isStaleInstance()
        || !cardWrapper.isConnected
        || cardWrapper.dataset.cardKey !== cardKey
      ) return;

      const sourceRect = cardWrapper.getBoundingClientRect();

      const preview = document.createElement('div');
      preview.id = 'card-hover-preview';
      preview.className = 'card-hover-preview';
      preview.style.zIndex = Cards.HOVER_PREVIEW_Z_INDEX || '2147483647';
      preview.style.left = `${Math.round(sourceRect.left)}px`;
      preview.style.top = `${Math.round(sourceRect.top)}px`;
      preview.style.visibility = 'hidden';

      const stellarAsset = variant === 'stellar' ? getStellarAsset(cardId) : null;
      const cardData = buildCardElementData(card, variant, stellarAsset);
      const cardEl = Cards.createCardElement(
        cardData,
        `moon-exchange-preview-${cardId}-${variant}`
      );
      cardEl.style.transformOrigin = 'center center';
      cardEl.style.setProperty('--card-hover-transform', 'scale(1) rotate(0deg) translateY(0%)');
      if (variant === 'stellar') {
        cardEl.dataset.stellarPreview = 'collection';
      }
      preview.appendChild(cardEl);

      const putDown = () => {
        if (variant === 'stellar' && Cards?.setStellarHover) {
          Cards.setStellarHover(cardEl, false);
        }
        preview.remove();
      };
      preview.addEventListener('mouseleave', () => {
        // Handed off, it no longer takes the pointer, so the next move reports
        // a leave; it stays until the neighbour's preview is revealed.
        if (HoverHandoff?.isHandedOff?.(preview)) return;
        putDown();
      });
      /* THE ENLARGED CARD IS STILL THE ROW'S CARD. The popup is a child of
         <body> that covers its source, so a click on it never reaches the row
         and used to do nothing but put the card down -- on the one part of the
         row a player actually aims at. Put it down, then do what a click on the
         row does. On touch this listener is never the actor: the tap latch
         swallows the enlarging tap's own click and answers the second tap by
         clicking the SOURCE, which the row hears directly. */
      if (typeof onActivate === 'function') {
        preview.style.cursor = 'pointer';
      }
      preview.addEventListener('click', () => {
        if (variant === 'stellar' && Cards?.setStellarHover) {
          Cards.setStellarHover(cardEl, false);
        }
        preview.remove();
        if (typeof onActivate === 'function') onActivate();
      });

      preview.addEventListener('wheel', (e) => {
        preview.style.pointerEvents = 'none';
        const target = document.elementFromPoint(e.clientX, e.clientY);
        preview.style.pointerEvents = 'auto';
        if (target) {
          const scrollable = target.closest('.moon-exchange-listings')
            || target.closest('.collection-section-exchange')
            || target.closest('.collection-content')
            || target.closest('.deck-builder-container')
            || target.closest('.deck-builder-cards');
          if (scrollable) scrollable.scrollTop += e.deltaY;
        }
      }, { passive: true });

      HoverHandoff?.bind?.(preview, {
        source: cardWrapper,
        selector: '.moon-exchange-card-preview',
        clear: putDown,
        handoff: true,
        release: putDown,
      });
      document.body.appendChild(preview);

      if (Cards?.applyPreviewScale) {
        // The popup adopts the resting card's description: same size step,
        // same lines (Cards: ENGRAVED DESCRIPTIONS).
        Cards.applyPreviewScale(cardEl, variant, sourceRect, preview, {
          ...Cards.HOVER_PREVIEW_OPTIONS,
          sourceCard,
        });
      }

      let settledRaster = false;
      try {
        settledRaster = await Cards.settleCardRasterForPresentation?.(
          cardEl,
          cardData,
          { oversample: 1, stableGeometry: true, sourceCard, coverVisualExpansion: true }
        ) === true;
      } catch (_) {
        settledRaster = false;
      }
      if (
        !settledRaster
        || !hoverActive
        || generation !== hoverGeneration
        || !isOpen
        || isStaleInstance()
        || !cardWrapper.isConnected
        || cardWrapper.dataset.cardKey !== cardKey
        // A tear-down from elsewhere (the shared clear, a screen's sweep)
        // took it. The settle refuses a card already off the page; this
        // covers the few microtasks between its last check and this line.
        || !preview.isConnected
      ) {
        preview.remove();
        return;
      }

      if (CardTilt?.registerPreview) {
        CardTilt.registerPreview(cardEl);
      }
      if (variant === 'stellar' && Cards?.setStellarHover) {
        Cards.setStellarHover(cardEl, true);
      }
      preview.style.removeProperty('visibility');
      // The preview this card took the hover from leaves in this task.
      HoverHandoff?.releaseHandedOff?.();
    };
    cardWrapper.addEventListener('mouseenter', (event) => {
      if (event?.isTrusted !== false && TouchPreview?.isSuppressingMouse?.()) return;
      // A card that came up under a resting pointer is not being pointed at.
      if (!hoverIntent.admits(event)) return;
      const hoverTarget = listingHoverTargets.get(cardWrapper);
      if (!hoverTarget) return;
      /* A neighbour's preview handed this card the hover and made itself
         transparent to the pointer; the browser then reports its own enter
         onto this card on the next move. That is the same hover, not a new
         one: restarting it would drop the build under way. */
      const handoff = HoverHandoff?.isHandoffEnter?.(event) ? {} : null;
      if (!handoff && handoffEnter) return;
      if (handoff) handoffEnter = handoff;
      const endHover = HoverHandoff?.beginHover?.();
      void presentHoverPreview(hoverTarget).finally(() => {
        if (handoffEnter === handoff) handoffEnter = null;
        endHover?.();
      });
    });
    TouchPreview?.attach?.(cardWrapper);

    cardWrapper.addEventListener('mouseleave', (e) => {
      hoverActive = false;
      hoverGeneration += 1;
      // The pointer really left: its next enter is a new hover.
      handoffEnter = null;
      const preview = currentHoverPreview();
      const related = e.relatedTarget instanceof Node ? e.relatedTarget : null;
      if (preview && related && (preview === related || preview.contains(related))) {
        return;
      }
      putDownPreview(preview);
    });
  }

  /* The preview the pointer can still own: the one holding the id. One handed
     to a neighbour (HoverHandoff) gave the id up and stays on screen by its
     class until the neighbour's own preview is revealed, so this answers with
     the one being built, or null before it exists. */
  function currentHoverPreview() {
    if (typeof HoverHandoff?.currentPreview === 'function') return HoverHandoff.currentPreview();
    return document.getElementById('card-hover-preview');
  }

  function putDownPreview(preview) {
    if (!preview) return;
    const previewCard = preview.querySelector('.card');
    if (previewCard?.classList?.contains('stellar-card') && Cards?.setStellarHover) {
      Cards.setStellarHover(previewCard, false);
    }
    preview.remove();
  }

  function createListingCardElement(card, listingId, variant) {
    const stellarAsset = variant === 'stellar' ? getStellarAsset(card.id || card.cardId) : null;
    const cardElement = Cards.createCardElement(
      buildCardElementData(card, variant, stellarAsset),
      `moon-exchange-${listingId}-${variant}`
    );
    cardElement.classList.add('deck-builder-card');
    if (variant === 'stellar') cardElement.dataset.stellarPreview = 'collection';
    return cardElement;
  }

  function hydrateListingStellarPreviews(nextListings) {
    const container = document.getElementById('moon-exchange-listings');
    if (!container || !Cards?.createCardElement) return;
    const cardById = faceCardsById();
    const listingById = new Map(
      Array.from(container.querySelectorAll('.moon-exchange-listing'))
        .map((element) => [element.dataset.listingId, element])
    );
    nextListings.forEach((listing) => {
      const card = listing?.isStellar ? cardById.get(listing.cardId) : null;
      const listingElement = listingById.get(String(listing?.id || ''));
      const preview = listingElement?.querySelector('.moon-exchange-card-preview');
      const cardElement = preview?.querySelector(':scope > .card');
      const stellarAsset = getStellarAsset(listing.cardId);
      if (!card || !cardElement || !stellarAsset) return;
      hydrateStellarMediaInPlace(cardElement, card, stellarAsset);
    });
  }

  async function loadListingStellarAssets(nextListings) {
    if (!StellarAssets?.load) return false;
    const ids = Array.from(new Set(
      (nextListings || [])
        .filter((listing) => listing && listing.isStellar)
        .map((listing) => listing.cardId)
        .filter(Boolean)
    ));
    if (ids.length === 0) return false;
    try {
      await StellarAssets.load(ids);
      return true;
    } catch (err) {
      console.warn('[MoonExchange] Failed to load stellar assets:', err);
      return false;
    }
  }

  function updateAvailability() {
    const select = document.getElementById('moon-exchange-card-select');
    const availability = document.getElementById('moon-exchange-availability');
    const qtyInput = document.getElementById('moon-exchange-quantity');
    const createBtn = document.getElementById('moon-exchange-create-btn');

    if (!select || !availability || !qtyInput || !createBtn) return;

    const { cardId, variant } = parseCardKey(select.value);
    const available = getOwnedQty(cardId, variant);
    availability.textContent = uiText('collection.exchange.available', 'Available: {{count}}', {
      count: available,
    });
    rememberShownSellCard(cardId, variant);

    if (available > 0) {
      qtyInput.max = String(available);
      if (!qtyInput.value || Number(qtyInput.value) > available) {
        qtyInput.value = '1';
      }
      createBtn.disabled = false;
    } else {
      qtyInput.max = '0';
      qtyInput.value = '';
      createBtn.disabled = true;
    }

    renderSellPreview(cardId, variant);
    updateSellerEstimate();
  }

  function buildCatalogSellableCards() {
    if (!MoonExchangeData) return [];
    return MoonExchangeData.buildSellableCards({
      allCards,
      ownedCardMap,
      ownedCardStellarMap,
    });
  }

  function hasCatalogContext() {
    return allCards.length > 0 || ownedCardMap.size > 0 || ownedCardStellarMap.size > 0;
  }

  function syncSellableCardsFromCatalog() {
    if (!catalogComplete) return;
    sellableCards = buildCatalogSellableCards();
    sellListSource = 'catalog';
  }

  const SELL_SEARCH_PAGE = 50;

  /** The sell column's cards for a collection too large to list whole: those matching the search, by name. */
  async function searchSellableCards(term = sellSearchTerm) {
    const generation = ++sellSearchGeneration;
    const rememberedKey = selectedSellKey();
    isLoadingSellableCards = true;
    sellableLoadError = null;
    renderSellOptions();
    try {
      const token = MoonfallCloud?.getAuthToken?.() || null;
      const params = new URLSearchParams({ search: term, pageSize: String(SELL_SEARCH_PAGE), lang: readerLanguage() });
      // A very large collection's index may still be preparing: the column
      // keeps its loading line until it answers, while this search is wanted.
      const response = await fetchPageAnswer(`/api/exchange/sellable?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }, { wanted: () => isOpen && generation === sellSearchGeneration });
      const body = response.ok && response.status !== 202 ? await response.json() : null;
      if (generation !== sellSearchGeneration) return;
      if (!body || !Array.isArray(body.items)) throw new Error(`sellable search failed: ${response.status}`);
      sellableCards = body.items;
      sellListSource = 'search';
    } catch (err) {
      if (generation !== sellSearchGeneration) return;
      console.error('[MoonExchange] Failed to search sellable cards:', err);
      sellableCards = [];
      sellListSource = null;
      sellableLoadError = uiText('collection.exchange.loadSellableFailed', 'Sell inventory is unavailable.');
    } finally {
      if (generation === sellSearchGeneration) {
        isLoadingSellableCards = false;
        renderSellOptions({ preferredKey: rememberedKey });
      }
    }
  }

  function syncSellSearchBox() {
    const box = /** @type {HTMLInputElement|null} */ (document.getElementById('moon-exchange-card-search'));
    if (!box) return;
    box.hidden = catalogComplete;
    if (!catalogComplete) return;
    sellSearchTerm = '';
    clearTimeout(sellSearchTimer);
    sellSearchTimer = null;
    if (box.value) box.value = '';
  }

  /* THE SELL COLUMN'S LAST CARD, KEPT (round 18, 28 September 2026).
     The column's card list is the owned catalog (`/api/cards`: 0.6-1.1 s on
     the WebKit 4G seat), read only on intent on the tab or at its press (the
     owner's rule: never a player's whole library ahead of intent). So on a
     new page — the meter's warm and revisit, a player's next session — the
     column's first frame waited for it, and past the grace the tab showed
     its loading state (decks17e: 0.14-0.36 s on every warm and revisit
     entry). The column now paints what it already holds: the card it last
     showed, kept in this browser for this account and language (the card's
     own words and picture, and its count then), and the list is reconciled
     in place when the catalog lands — the same option and the same card,
     unless the holdings changed. A count stale by then is only ever shown:
     the server decides what may be listed (`createListing` refuses more
     prints than the account holds, in its own transaction). Only a card
     shown from the catalog, on the open tab, is kept: the server's list
     names cards in English. A card asked for by name (the Collection's
     "List on Exchange") that is not the kept one paints nothing ahead: the
     column waits for the catalog, as before, and opens on that card. */
  const SELL_CARD_STORAGE_KEY = 'moonfall_exchange_sell_card';
  const SELL_CARD_FIELDS = [
    'cardId', 'variant', 'name', 'baseName', 'image', 'description', 'collection',
    'cost', 'type', 'character', 'targetingType', 'available',
  ];
  let rememberedSellText = '';

  function readerLanguage() {
    return i18n?.getLanguage?.() || 'en';
  }

  /** The column's list while the catalog is on its way: the kept card, when it is the one wanted. */
  function rememberedSellCards() {
    if (!currentUserId) return [];
    let record = null;
    try {
      record = JSON.parse(localStorage.getItem(SELL_CARD_STORAGE_KEY) || 'null');
    } catch (_) {
      return [];
    }
    const entry = record?.entry;
    if (
      record?.owner !== currentUserId
      || record?.lang !== readerLanguage()
      || typeof entry?.cardId !== 'string'
      || typeof entry.name !== 'string'
      || !entry.name
      || !(Number(entry.available) > 0)
    ) return [];
    const variant = entry.variant === 'stellar' ? 'stellar' : 'normal';
    if (
      pendingSelection
      && buildCardKey(pendingSelection.cardId, pendingSelection.variant) !== buildCardKey(entry.cardId, variant)
    ) return [];
    return [{ ...entry, variant, isStellar: variant === 'stellar' }];
  }

  /** Keep the card the open column shows from the catalog, for the next entry. */
  function rememberShownSellCard(cardId, variant) {
    if (!isOpen || sellListSource !== 'catalog' || !cardId || !currentUserId) return;
    // Its words are kept under the reader's language: a catalog in another
    // language (kept across a language change) is not.
    if (!catalogInReaderLanguage()) return;
    const shown = sellableCards.find((entry) => entry.cardId === cardId && entry.variant === variant);
    if (!shown) return;
    const entry = {};
    SELL_CARD_FIELDS.forEach((field) => {
      if (shown[field] !== undefined) entry[field] = shown[field];
    });
    const text = JSON.stringify({ owner: currentUserId, lang: readerLanguage(), entry });
    if (text === rememberedSellText) return;
    try {
      localStorage.setItem(SELL_CARD_STORAGE_KEY, text);
      rememberedSellText = text;
    } catch (_) {
      // Storage refused (a private window, a full store): the next entry waits for the catalog.
    }
  }

  /** Whether the column's list is the one it keeps: the catalog's or the server's, on the open tab. */
  function sellListFinal() {
    return isOpen
      && !catalogPending
      && !isLoadingSellableCards
      && (sellListSource === 'catalog' || sellListSource === 'server');
  }

  /**
   * Make the select's options these, keeping every option that stays — the
   * one on show above all — so a list that grows around the chosen card (the
   * catalog landing on the kept card) changes nothing on screen.
   * @param {HTMLSelectElement} select
   * @param {string[]} keys
   * @param {string[]} labels
   */
  function reconcileSellOptions(select, keys, labels) {
    const kept = new Map();
    Array.from(select.options).forEach((option) => {
      if (option.value && !kept.has(option.value)) kept.set(option.value, option);
      else option.remove();
    });
    keys.forEach((key, index) => {
      let option = kept.get(key);
      if (option) {
        kept.delete(key);
      } else {
        option = document.createElement('option');
        option.value = key;
      }
      if (option.textContent !== labels[index]) option.textContent = labels[index];
      if (select.options[index] !== option) select.insertBefore(option, select.options[index] || null);
    });
    kept.forEach((option) => option.remove());
  }

  /**
   * @param {object} [options]
   * @param {string} [options.preferredKey] the card to keep chosen when the
   *   select has no choice of its own left (a re-read that meanwhile had only
   *   "Loading..." to show)
   */
  function renderSellOptions({ preferredKey = '' } = {}) {
    const select = /** @type {HTMLSelectElement|null} */ (
      document.getElementById('moon-exchange-card-select')
    );
    if (!select) return;

    const options = Array.isArray(sellableCards) ? sellableCards : buildCatalogSellableCards();
    // A catalog on its way is a list still loading, never "no cards".
    const listLoading = isLoadingSellableCards || catalogPending;

    // Empty state: with nothing to sell, swap the dead form (+ its black card
    // preview box) for a friendly "no cards yet" message so the panel reads as
    // intentional rather than broken.
    const hasLoadError = Boolean(sellableLoadError) && !listLoading;
    // A search that found nothing is not an empty collection: the form, with
    // the box being typed in, stays, and the list says nothing matched.
    const searchMiss = !catalogComplete && sellSearchTerm !== '' && options.length === 0 && !listLoading && !hasLoadError;
    const isEmpty = options.length === 0 && !listLoading && !isLoading && !hasLoadError && !searchMiss;
    const emptyEl = document.getElementById('moon-exchange-sell-empty');
    const errorEl = document.getElementById('moon-exchange-sell-error');
    const formEl = document.querySelector('.moon-exchange-sell .moon-exchange-form');
    /* THE LOCK OUTRANKS THE EMPTY STATE, because two owners were writing the
       same `hidden` attribute and the later writer won. This function swaps the
       form for "no cards yet"; `applySellUnlockState` swaps it for the locked
       panel. Reloading the sell options after the gate had run therefore
       re-showed the Create form to an account the server will always refuse —
       a control the product does not honour, which is the one thing a gate may
       never do. It surfaced as the form staying visible for a full 10s beside
       an already-visible locked panel, at one landscape page box in Firefox.
       Ask the gate first and let it keep the form hidden. */
    const sellLocked = sellUnlock.unlocked !== true;
    if (emptyEl) emptyEl.hidden = sellLocked || !isEmpty;
    /* THE LOCK NEVER SUPPRESSES AN ERROR. It hides the form and the "no cards
       yet" state, because neither is actionable while the gate is closed — but
       a load failure is information the player needs either way, and silencing
       it would turn a real failure into a blank panel. Caught by
       `moonExchange.integration.test.js`, which asserts that an authoritative
       load failure and a genuinely empty marketplace look different. */
    if (errorEl) errorEl.hidden = !hasLoadError;
    if (formEl) formEl.hidden = sellLocked || isEmpty || hasLoadError;

    /* A RE-READ KEEPS WHAT IS ALREADY LISTED. "Loading..." is for a list with
       nothing in it yet. A purchase re-reads the list from the server, and
       writing the loading line over a full list read as availability 0, no
       quantity and a disabled Create for the whole round trip; the answer then
       landed on the first card instead of the player's. */
    if (options.length === 0 && (listLoading || isLoading)) {
      renderedSellOptionsMarkup = '';
      select.innerHTML = `<option value="">${escapeHtml(uiText('status.loading', 'Loading'))}...</option>`;
    } else if (options.length === 0) {
      renderedSellOptionsMarkup = '';
      select.innerHTML = searchMiss
        ? `<option value="">${escapeHtml(uiText('collection.exchange.noSellableMatch', 'No card you can sell matches that name'))}</option>`
        : `<option value="">${escapeHtml(uiText('collection.exchange.noSellableCards', 'No eligible cards to sell yet'))}</option>`;
    } else {
      const previousValue = select.value || preferredKey;
      const keys = options.map((entry) => buildCardKey(entry.cardId, entry.variant));
      const labels = options.map((entry) => `${entry.name} (x${entry.available})`);
      const markup = keys.map((key, index) => `${key}\u0000${labels[index]}`).join('\u0001');
      // Every re-render of the column lands here; a list that would print the
      // same options, and still holds them, is left as it is, and any other
      // is reconciled in place.
      const listed = select.options.length === keys.length
        && keys.every((key, index) => select.options[index].value === key);
      if (markup !== renderedSellOptionsMarkup || !listed) {
        reconcileSellOptions(select, keys, labels);
        renderedSellOptionsMarkup = markup;
      }
      const nextValue = previousValue && keys.includes(previousValue) ? previousValue : keys[0];
      if (select.value !== nextValue) select.value = nextValue;
    }

    updateAvailability();
    applyPendingSelection();
  }

  async function refreshSellableCards({ forceRemote = false } = {}) {
    if (!catalogComplete) {
      await searchSellableCards();
      return;
    }
    // The catalog is on its way (the tab opened before it): the column waits
    // for it. The remote list names the cards in English.
    if (catalogPending && !forceRemote) {
      renderSellOptions();
      return;
    }
    const canUseCatalog = hasCatalogContext();
    if (!forceRemote && canUseCatalog) {
      syncSellableCardsFromCatalog();
      renderSellOptions();
      return;
    }

    if (!MoonfallCloud?.moonExchange?.listSellableCards) {
      syncSellableCardsFromCatalog();
      renderSellOptions();
      return;
    }

    // The player's card outlives the read, even if the list meanwhile had
    // nothing to show and the select kept no choice of its own.
    const rememberedKey = selectedSellKey();
    isLoadingSellableCards = true;
    sellableLoadError = null;
    renderSellOptions();

    let tooManyToList = false;
    try {
      const nextCards = await MoonfallCloud.moonExchange.listSellableCards();
      sellableCards = Array.isArray(nextCards) ? nextCards : [];
      sellListSource = 'server';
      sellableLoadError = null;
    } catch (err) {
      // A collection too large to list whole: the column searches instead.
      if (err?.code === 'too_many_items') {
        tooManyToList = true;
        return;
      }
      console.error('[MoonExchange] Failed to load sellable cards:', err);
      if (canUseCatalog) {
        syncSellableCardsFromCatalog();
      } else {
        sellableCards = [];
        sellListSource = null;
        sellableLoadError = uiText('collection.exchange.loadSellableFailed', 'Sell inventory is unavailable.');
      }
    } finally {
      isLoadingSellableCards = false;
      if (tooManyToList) {
        catalogComplete = false;
        syncSellSearchBox();
        void searchSellableCards('');
      } else {
        renderSellOptions({ preferredKey: rememberedKey });
      }
    }
  }

  async function refreshInventory() {
    await refreshSellableCards();
  }

  function applyPendingSelection() {
    if (!pendingSelection) return false;
    const select = document.getElementById('moon-exchange-card-select');
    if (!select) return false;

    const { cardId, variant, quantity } = pendingSelection;
    const key = buildCardKey(cardId, variant);
    const optionExists = Array.from(select.options).some((option) => option.value === key);
    /* KEPT UNTIL THE LIST THAT STAYS. The Collection asks for the card
       ("List on Exchange") before the tab opens, and the column's list is
       then still on its way (the catalog), the Collection's paged cards, or
       the card the column last showed: each dropped the choice, and the
       column opened on its first card instead (found in round 18; since read
       audit F3 the tab opens before the catalog). */
    if (!optionExists) {
      if (sellListFinal()) pendingSelection = null;
      return false;
    }

    select.value = key;
    updateAvailability();

    const available = getOwnedQty(cardId, variant);
    if (available > 0) {
      const qtyInput = document.getElementById('moon-exchange-quantity');
      if (qtyInput) {
        const desiredQty = Number.isFinite(Number(quantity)) ? Math.floor(Number(quantity)) : 1;
        const safeQty = Math.max(1, Math.min(desiredQty || 1, available));
        qtyInput.value = String(safeQty);
      }
    }

    if (sellListFinal()) pendingSelection = null;
    return true;
  }

  function prefillSellForm({ cardId, variant = 'normal', quantity = 1 } = {}) {
    if (!cardId) return false;
    setMobilePane('sell');
    pendingSelection = {
      cardId,
      variant: variant === 'stellar' ? 'stellar' : 'normal',
      quantity
    };
    return applyPendingSelection();
  }

  function showLoadingSkeleton() {
    const container = document.getElementById('moon-exchange-listings');
    if (!container) return;
    // First paint must use the lane's real geometry too. Otherwise the default
    // API limit briefly creates more skeleton rows than the restored desktop
    // list can display, which looks like clipped/vanishing listings.
    syncListingsPageSize();
    // Behind an entry hold nothing is painted, and the rows the last visit
    // committed are exactly what this entry's render reconciles against:
    // replacing them with skeletons would only force a rebuild.
    if (entryHold?.active && laneHasCommittedContent()) return;
    container.setAttribute('aria-busy', 'true');
    container.classList.remove('me-loaded');
    delete container.dataset.renderSignature;
    disposeExchangeExactImages(container);
    container.innerHTML = '';

    const select = document.getElementById('moon-exchange-card-select');
    if (select && select.options.length <= 1 && !select.value) {
      select.innerHTML = '';
      const opt = document.createElement('option');
      opt.value = '';
      opt.textContent = uiText('status.loading', 'Loading') + '...';
      select.appendChild(opt);
    }
    const summary = document.getElementById('moon-exchange-summary');
    if (summary) summary.textContent = uiText('collection.exchange.loadingListings', 'Loading listings...');
    const createBtn = document.getElementById('moon-exchange-create-btn');
    if (createBtn) createBtn.disabled = true;
    const count = Math.max(1, Math.min(activePageSize, 4));
    for (let i = 0; i < count; i++) {
      const skel = document.createElement('div');
      skel.className = 'moon-exchange-skeleton';

      const card = document.createElement('div');
      card.className = 'moon-exchange-skeleton-card';
      const thumb = document.createElement('div');
      thumb.className = 'moon-exchange-skeleton-thumb';
      const lines = document.createElement('div');
      lines.className = 'moon-exchange-skeleton-lines';
      for (let j = 0; j < 3; j++) {
        const line = document.createElement('div');
        line.className = 'moon-exchange-skeleton-line';
        lines.appendChild(line);
      }
      card.appendChild(thumb);
      card.appendChild(lines);

      const action = document.createElement('div');
      action.className = 'moon-exchange-skeleton-action';
      const price = document.createElement('div');
      price.className = 'moon-exchange-skeleton-price';
      const btn = document.createElement('div');
      btn.className = 'moon-exchange-skeleton-btn';
      action.appendChild(price);
      action.appendChild(btn);

      skel.appendChild(card);
      skel.appendChild(action);
      container.appendChild(skel);
    }
  }

  async function renderListings() {
    const generation = ++listingsRenderGeneration;
    /* A row whose card this surface does not hold yet, while the catalog is
       on its way (the tab opened before it): wait for the catalog, capped,
       rather than draw the row's bare picture that its face would replace a
       moment later. The catalog's arrival renders the page itself. */
    if (catalogPending && listingsMissFaces(listings)) {
      await catalogLandedWithin(EXCHANGE_REVEAL_CAP_MS);
      if (generation !== listingsRenderGeneration) return false;
    }
    // The real-money equivalent has to be resolvable at paint time; fetch the
    // reference rate once and, if it only arrives after the rows are up, print
    // it into their own money line — never by rebuilding the page, which would
    // tear down every settled raster to add one line of text.
    if (Stardust && Stardust.moneyEquivalent(1) == null) {
      Stardust.loadReferenceRate?.().then((rate) => {
        if (!rate) return;
        document.querySelectorAll('#moon-exchange-listings > .moon-exchange-listing').forEach((row) => {
          const money = row.querySelector('.moon-exchange-card-money');
          const unitPrice = Number(row.getAttribute('data-unit-price'));
          const text = Stardust.moneyEquivalent?.(unitPrice) || '';
          if (money && money.textContent !== text) money.textContent = text;
        });
      });
    }
    const listingsSnapshot = listings;
    const cardsSnapshot = allCards;
    const container = document.getElementById('moon-exchange-listings');
    const summary = document.getElementById('moon-exchange-summary');
    if (!container || !MoonExchangeData) return;
    const priorStage = container.querySelector(':scope > .moon-exchange-paint-stage');
    disposeExchangeExactImages(priorStage);
    priorStage?.remove();

    const cardById = faceCardsById();
    // Named from the cards the faces are drawn from, in the reader's language.
    const view = MoonExchangeData.buildListingView({
      listings,
      allCards: [...cardById.values()],
      currentUserId,
      stellarLabel: uiText('common.stellar', 'Stellar'),
    });

    /* RECONCILE, NEVER REBUILD, AN UNCHANGED PAGE. Everything a row prints —
       the listing, whether its card face is known, the buy lock, the words'
       language, the geometry its raster was settled at — is in this
       signature. A render that would print the same page (a context sync, a
       re-read, a revisit) keeps every committed row, its settled raster and
       its hover/tilt bindings; only the affordability, which follows the
       balance, is refreshed. */
    const renderSignature = JSON.stringify([
      localeRevision,
      viewportRenderSignature,
      currentUserId,
      sellUnlock.unlocked,
      sellUnlock.required,
      currentOwnerFilter,
      searchText.trim(),
      listingsLoadError || '',
      isLoading && view.length === 0,
      Boolean(Stardust?.moneyEquivalent?.(1)),
      view.map((entry) => [
        entry.id,
        entry.cardId,
        entry.isStellar,
        entry.unitPrice,
        entry.quantityRemaining,
        entry.isMine,
        entry.sellerName,
        entry.cardName,
        entry.cardImage,
        cardById.has(entry.cardId),
      ]),
    ]);

    if (summary) {
      if (isLoading && view.length === 0) {
        summary.textContent = uiText('collection.exchange.loadingListings', 'Loading listings...');
      } else {
        const isSearchBackfilling = searchBackfillStatus === 'backfilling';
        if (searchText.trim()) {
          const key = currentOwnerFilter === 'mine'
            ? 'collection.exchange.summaryMineSearch'
            : currentOwnerFilter === 'others'
              ? 'collection.exchange.summaryOthersSearch'
              : 'collection.exchange.summarySearch';
          const searchNote = isSearchBackfilling
            ? uiText('collection.exchange.searchIndexing', ' · Search indexing...')
            : '';
          summary.textContent = uiText(key, 'Showing {{count}} results on this page{{note}}', {
            count: view.length,
            note: searchNote,
          });
        } else {
          const key = currentOwnerFilter === 'mine'
            ? 'collection.exchange.summaryMine'
            : currentOwnerFilter === 'others'
              ? 'collection.exchange.summaryOthers'
              : 'collection.exchange.summary';
          summary.textContent = uiText(key, 'Showing {{count}} on this page', {
            count: view.length,
          });
        }
      }
    }

    if (
      container.dataset.renderSignature === renderSignature
      && container.childElementCount > 0
    ) {
      container.querySelectorAll(':scope > .moon-exchange-listing').forEach(syncListingAffordability);
      container.setAttribute('aria-busy', 'false');
      return true;
    }
    // A closed Exchange never commits rows (see the check after the stage
    // settles), so it does not build and settle a page only to drop it — a
    // context sync on the way back into the tab would otherwise do exactly
    // that before open() renders the page for real. Nor the empty line: a
    // context sync before any page was read (the Collection's open)
    // committed "No open listings yet." with a signature, and the entry,
    // its own render superseded by the catalog's landing, took that for its
    // committed lane and was revealed on it, the rows popping in ~0.37 s
    // later (round 18, offline, Chrome and the WebKit phone).
    if (!isOpen) return false;

    if (view.length === 0) {
      disposeExchangeExactImages(container);
      container.innerHTML = '';
      delete container.dataset.renderSignature;
      container.classList.remove('me-loaded');
      container.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      if (isLoading) {
        showLoadingSkeleton();
        return;
      }
      container.dataset.renderSignature = renderSignature;
      if (listingsLoadError) {
        const errorEl = document.createElement('div');
        errorEl.className = 'moon-exchange-market-error';
        errorEl.setAttribute('role', 'alert');
        const message = document.createElement('p');
        message.textContent = listingsLoadError;
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'btn deck-btn';
        retry.textContent = uiText('collection.exchange.retry', 'Retry');
        retry.addEventListener('click', () => refreshListings({ reset: true }));
        errorEl.append(message, retry);
        container.appendChild(errorEl);
        return;
      }
      const emptyMsg = document.createElement('div');
      emptyMsg.className = 'moon-exchange-empty';
      if (currentOwnerFilter === 'mine') {
        emptyMsg.textContent = searchText.trim()
          ? uiText('collection.exchange.emptyMineSearch', 'No personal listings match your search.')
          : uiText('collection.exchange.emptyMine', 'You have no open listings.');
      } else if (currentOwnerFilter === 'others') {
        emptyMsg.textContent = searchText.trim()
          ? uiText('collection.exchange.emptyOthersSearch', 'No other listings match your search.')
          : uiText('collection.exchange.emptyOthers', 'No open listings from other players.');
      } else {
        emptyMsg.textContent = searchText.trim()
          ? uiText('collection.exchange.emptySearch', 'No listings match your search.')
          : uiText('collection.exchange.empty', 'No open listings yet.');
      }
      container.appendChild(emptyMsg);
      return;
    }

    container.setAttribute('aria-busy', 'true');
    container.dataset.rasterState = 'settling';
    const stage = document.createElement('div');
    stage.className = 'moon-exchange-listings moon-exchange-paint-stage';
    stage.setAttribute('aria-hidden', 'true');
    container.appendChild(stage);
    const rasterSettlements = [];

    /* A ROW THAT PRINTS THE SAME IS KEPT (round 18b, 29 September 2026). A
       page read again behind the view (a re-entry, a page turn onto a kept
       page) differs from the one on screen by a listing or two — one sold,
       one listed, a price changed — and the whole page was built again,
       every row's card re-rastered, to change them. The rows are keyed by
       listing now: a committed row whose listing, card face, language,
       viewport and lock print the same is kept, element and raster; only a
       new or changed row is built and settled; the lane then takes its new
       order in one step. */
    const rowContext = JSON.stringify([
      localeRevision,
      viewportRenderSignature,
      currentUserId,
      sellUnlock.unlocked,
      sellUnlock.required,
      Boolean(Stardust?.moneyEquivalent?.(1)),
    ]);
    /** @type {Map<string, HTMLElement>} */
    const committedRows = new Map();
    /** @type {NodeListOf<HTMLElement>} */ (container.querySelectorAll(':scope > .moon-exchange-listing')).forEach((row) => {
      if (row.dataset.rowSignature && row.dataset.listingId) committedRows.set(row.dataset.listingId, row);
    });
    const rows = [];

    view.forEach((entry) => {
      const rowSignature = `${rowContext}|${JSON.stringify([
        entry.id,
        entry.cardId,
        entry.isStellar,
        entry.unitPrice,
        entry.quantityRemaining,
        entry.isMine,
        entry.sellerName,
        entry.cardName,
        entry.cardImage,
        cardById.has(entry.cardId),
      ])}`;
      const committed = committedRows.get(entry.id);
      if (committed?.dataset.rowSignature === rowSignature) {
        committedRows.delete(entry.id);
        rows.push(committed);
        return;
      }
      const listingEl = document.createElement('div');
      listingEl.className = `moon-exchange-listing${entry.isMine ? ' moon-exchange-listing-mine' : ''}`;
      listingEl.dataset.rowSignature = rowSignature;
      listingEl.dataset.listingId = entry.id;
      listingEl.dataset.cardId = entry.cardId;
      listingEl.dataset.unitPrice = String(entry.unitPrice);

      const cardEl = document.createElement('div');
      cardEl.className = 'moon-exchange-card';

      /* THE ROW OPENS THE LISTING. Sharing a link lands the reader on exactly
         this view, so the same view has to be reachable by clicking a row --
         otherwise the shared link shows a screen no player has ever seen. One
         opener for every way in through this row: the row itself and the
         card's own enlargement (see `attachListingHoverPreview`). */
      const openThisListing = () => {
        openListingDetail(entry, {
          door: 'row',
          /* The entry now carries the card's name, rules text, cost and type
             from the SERVER, so this local row is only a source of ART. It used
             to be the only source of everything, and a card missing from this
             client's catalogue rendered as its raw `cardId` with no rules
             text — on the row and, through this view, on the listing screen. */
          card: faceCardFor(entry, cardById),
          /* THE SAME FACT-SET THE LINK DOOR BUILDS. Everything the view needs
             to know about the reader, resolved — the Exchange is behind a
             login, the row already knows whether the listing is theirs, and
             the grid already tracks the balance for its own affordance checks.
             So this door never has a `resolving` moment. */
          viewer: {
            state: currentUserId ? 'player' : 'guest',
            isMine: entry.isMine === true,
            /* THE TOP BAR, which by the time this grid is on screen has long
               since painted. It was the grid's private `stardust`, which is
               only set when the Exchange is initialised with one and is
               otherwise not a number at all, so the balance line vanished on
               this door while the other showed it -- the two screens
               disagreeing about the one fact a buyer decides on.
               The link door cannot use this: it runs before the top bar paints,
               so it takes the same figure from `listingForViewer` instead.
               Different sources, one number, and `undefined` means the same
               thing on both doors -- no line. */
            stardustBalance: displayedStardust(),
            /* THE SAME GATE THE PANEL NEEDS. The account-level unlock binds
               buying as well as selling, so the row door hands over what it
               already tracks for its own sell panel -- otherwise this door
               offers a live Buy that the server refuses, which is the defect
               reported on the link door. */
            exchangeUnlock: sellUnlock.unlocked !== true && sellUnlock.required > 0
              ? { unlocked: false, finished: sellUnlock.finished, required: sellUnlock.required }
              : null,
          },
          /* The same mutation the row's own button calls, followed by the same
             bookkeeping. It is not a re-press of that button: the row's status
             messages render UNDERNEATH this dialog, so the purchase the reader
             started here would have reported its result somewhere they cannot
             see. */
          buy: buyFromListingView,
          cancel: cancelFromListingView,
        });
      };

      const cardPreview = document.createElement('div');
      cardPreview.className = 'moon-exchange-card-preview';
      const cardData = faceCardFor(entry, cardById);
      const variant = entry.isStellar ? 'stellar' : 'normal';
      if (Cards?.createCardElement && cardData) {
        const previewCard = createListingCardElement(cardData, entry.id, variant);
        const previewData = buildCardElementData(
          cardData,
          variant,
          variant === 'stellar' ? getStellarAsset(entry.cardId) : null
        );
        if (variant === 'stellar') {
          cardPreview.classList.add('deck-card-item-stellar');
        }
        cardPreview.appendChild(previewCard);
        /* `deck-builder.css` marks the card inside a listing
           `pointer-events: none` so the tile owns the gesture, exactly like the
           other catalogs. CardTilt's observer binds its listeners to the CARD,
           which then never receives a pointer event and never tilts — Moon
           Exchange was the one catalog that never handed it the tile as host
           (card-packs, deck-builder and collection all do), so its listings had
           a glare layer and no 3D at all. */
        if (CardTilt?.bindHost) {
          CardTilt.bindHost(cardPreview, previewCard);
        }
        rasterSettlements.push(() => (
          settleExchangeCard(previewCard, previewData, cardPreview)
        ));

        /* Clicking the card -- at rest, where the row hears it, or ENLARGED,
           where only the popup does -- opens the listing. */
        attachListingHoverPreview(cardPreview, cardData, variant, { onActivate: openThisListing });
      } else {
        const img = document.createElement('img');
        img.className = 'moon-exchange-card-image';
        img.dataset.exchangeArtOwner = 'true';
        img.alt = entry.cardName;
        img.loading = 'eager';
        cardPreview.appendChild(img);
        rasterSettlements.push(async () => {
          if (!(img.naturalWidth > 0 && img.naturalHeight > 0)) {
            try {
              await img.decode?.();
            } catch (_) {
              return false;
            }
          }
          return img.naturalWidth > 0 && img.naturalHeight > 0;
        });
        if (entry.cardImage) {
          loadExchangeExactImage(img, entry.cardImage, {
            fit: cardData?.type === 'Character' ? 'cover' : 'contain',
          });
        } else if (entry.cardId && typeof Cards?.resolveCardArt === 'function') {
          void Cards.resolveCardArt(
            { id: entry.cardId, imageUrl: undefined },
            img,
            {
              directArtOnly: true,
              rasterReserveScale: 1,
              fit: cardData?.type === 'Character' ? 'cover' : 'contain',
            },
          );
        } else {
          img.classList.add('card-art-unavailable');
        }
      }

      const info = document.createElement('div');
      info.className = 'moon-exchange-card-info';
      info.innerHTML = `
        <div class="moon-exchange-card-name">${escapeHtml(entry.cardName)}</div>
        <div class="moon-exchange-card-meta" title="${escapeHtml(uiText('collection.exchange.priceRemaining', 'Price: {{price}} Stardust · Remaining: {{remaining}}', {
          price: entry.unitPrice,
          remaining: entry.quantityRemaining,
        }))}">${escapeHtml(uiText('collection.exchange.priceRemaining', 'Price: {{price}} Stardust · Remaining: {{remaining}}', {
          price: formatStardust(entry.unitPrice),
          remaining: entry.quantityRemaining,
        }))}</div>
        <div class="moon-exchange-card-money">${escapeHtml(
          Stardust?.moneyEquivalent?.(entry.unitPrice) || ''
        )}</div>
        <div class="moon-exchange-card-seller">${escapeHtml(uiText('collection.exchange.seller', 'Seller: {{seller}}', {
          seller: entry.sellerName,
        }))}</div>
      `;
      cardEl.appendChild(cardPreview);
      cardEl.appendChild(info);

      const buyEl = document.createElement('div');
      buyEl.className = 'moon-exchange-buy';

      /* BROWSING IS OPEN, BUYING IS NOT — owner's decision, 2026-08-25.
         A locked account should be able to see what the Exchange is before
         earning it, so the listings render in full. What it may not do is buy,
         and the server refuses that with `exchange_locked` whatever this
         button looks like. The button is how the player LEARNS the rule
         instead of discovering it through a failure. */
      const buyLocked = !entry.isMine && sellUnlock.unlocked === false;

      if (!entry.isMine && !buyLocked) {
        const qtyInput = document.createElement('input');
        qtyInput.type = 'number';
        qtyInput.className = 'deck-builder-search moon-exchange-buy-qty';
        qtyInput.min = '1';
        qtyInput.max = String(entry.quantityRemaining);
        qtyInput.value = '1';
        buyEl.appendChild(qtyInput);
      }

      const total = document.createElement('div');
      total.className = 'moon-exchange-buy-total';
      renderStardustText(
        total,
        'collection.exchange.total',
        'Total: {{total}} Stardust',
        entry.unitPrice,
        'total',
      );

      const buyBtn = document.createElement('button');
      if (entry.isMine) {
        buyBtn.className = 'btn deck-btn deck-btn-danger moon-exchange-cancel-btn';
        buyBtn.textContent = uiText('collection.exchange.cancelListing', 'Cancel listing');
      } else if (buyLocked) {
        /* Locked reads as a padlocked control that SAYS the condition, rather
           than a greyed button with no explanation. `title` and the accessible
           name carry the same sentence, so it is not a visual-only cue. */
        buyBtn.className = 'btn deck-btn moon-exchange-buy-btn moon-exchange-buy-btn--locked';
        const label = uiText(
          'collection.exchange.lockedBuy',
          'Win {{required}} matches to buy',
          { required: sellUnlock.required },
        );
        buyBtn.textContent = `\u{1F512} ${uiText('collection.exchange.buy', 'Buy')}`;
        buyBtn.title = label;
        buyBtn.setAttribute('aria-label', label);
      } else {
        buyBtn.className = 'btn deck-btn moon-exchange-buy-btn';
        buyBtn.textContent = uiText('collection.exchange.buy', 'Buy');
      }
      buyBtn.disabled = buyLocked;

      buyEl.appendChild(total);
      buyEl.appendChild(buyBtn);

      /* The controls inside the row keep working: the handler ignores clicks
         that landed on a button or a field, so Buy and the quantity input are
         not swallowed by the row. */
      cardEl.style.cursor = 'pointer';
      cardEl.addEventListener('click', (event) => {
        /* `event.target` is an EventTarget, which has no `closest` -- a text
           node inside the row is a real possibility, so this is a guard and not
           only a type cast. */
        const hit = event.target instanceof Element ? event.target : null;
        if (hit?.closest('button, input, select, a')) return;
        openThisListing();
      });

      /* SHARE IS ON EVERY ROW, INCLUDING YOUR OWN — especially your own, since
         the point of posting is to be found, and a seller should not have to
         open a listing to share it. It has its OWN grid cell: it lived in the
         `action` cell once and painted on top of Buy. */
      const shareBtn = document.createElement('button');
      shareBtn.type = 'button';
      shareBtn.className = 'deck-btn deck-btn-secondary moon-exchange-share';
      shareBtn.dataset.i18n = 'exchange.share.button';
      /* A LABEL, NOT A GLYPH. An arrow was tried to buy horizontal room and is
         not what this control should be — a share action that has to be
         guessed at is a worse control than a wide one. The room comes from the
         LAYOUT instead: Share takes its own row under Buy rather than a third
         column, so the actions column gets NARROWER than the three-column
         version and the card side keeps its width. The extra row is free
         wherever the 118px card preview already sets the row height, which is
         every tier except the landscape phone, where Share is hidden and the
         row itself opens the listing.
         No wrapper spans: a hidden button's child span reports a 0x0 box at the
         origin and `exchange.real.spec.js` counts it as a descendant out of
         bounds. */
      shareBtn.textContent = uiText('exchange.share.button', 'Share listing');
      shareBtn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        openListingShare(entry);
      });
      buyEl.appendChild(shareBtn);

      listingEl.appendChild(cardEl);
      listingEl.appendChild(buyEl);

      stage.appendChild(listingEl);
      rows.push(listingEl);
      syncListingAffordability(listingEl);
    });

    // Bounded atomic commit (docs/asset-loading.md law 2 + cohort rule): the
    // listings appear together when every raster settles, or at the cap with
    // per-card art-well placeholders. A slow or failed raster degrades that
    // one listing, never the whole browse grid.
    const settled = await Promise.race([
      Promise.all(rasterSettlements.map((settle) => settle())),
      new Promise((resolve) => {
        setTimeout(() => resolve('reveal-cap'), EXCHANGE_REVEAL_CAP_MS);
      }),
    ]);
    if (
      generation !== listingsRenderGeneration
      || listings !== listingsSnapshot
      || allCards !== cardsSnapshot
      || !isOpen
      || isStaleInstance()
      || !stage.isConnected
      || document.getElementById('moon-exchange-listings') !== container
    ) {
      disposeExchangeExactImages(stage);
      stage.remove();
      return false;
    }
    if (Array.isArray(settled) && settled.some((ready) => ready !== true)) {
      console.warn('[MoonExchange] Some listing rasters did not settle; committing with per-card placeholders.');
    }

    // Rows replacing rows are a refresh; rows replacing nothing on screen
    // (the empty line, skeletons) are the lane's entrance. Rows committed
    // behind the entry hold are revealed WITH the section, in its one frame:
    // a fade there started while the body was held (or frames after the
    // reveal on a busy main thread) and faded the rows in behind the finished
    // section (snappy round 15 re-run: a 122 ms pop-in, Chrome desktop cold).
    const hadRows = Boolean(container.querySelector(':scope > .moon-exchange-listing'));
    const revealedWithSection = Boolean(entryHold?.active);
    // In one step: what leaves goes, the new rows take their places, and a
    // kept row moves only when its place changed.
    const staying = new Set(rows);
    stage.remove();
    Array.from(container.children).forEach((child) => {
      if (staying.has(child)) return;
      disposeExchangeExactImages(child);
      child.remove();
    });
    rows.forEach((row, index) => {
      if (container.children[index] !== row) container.insertBefore(row, container.children[index] || null);
    });
    container.dataset.renderSignature = renderSignature;
    container.classList.add('me-loaded');
    // New rows under a resting pointer are not being pointed at.
    hoverIntent.disarm();
    if (!hadRows && !revealedWithSection) playLaneEntrance(container);
    container.dataset.rasterState = 'ready';
    container.setAttribute('aria-busy', 'false');
    return true;
  }

  /* THE SELL COLUMN IS PRINTED WITH ITS ANSWER, NOT BEFORE IT.

     Measured on the real backend (exchange-open-timeline.real.spec.js): the
     Exchange section was on screen at 0 ms and the sell column stayed empty
     until the config read answered at ~650 ms — then the sell form (or the
     "opens after N wins" card) popped in. The owner: "I saw moon exchange
     morph. it checks if user unlocked moon exchange which causes this morph."

     The read is now memoised and warmed ahead of the tab — when the lobby
     goes idle and again when the Collection opens — and `prepare()` applies
     the cached answer BEFORE the section is displayed, so the first frame
     already carries the right column. `open()` still re-reads, so a change
     (the unlock itself) reaches the surface the same way it always has. */
  const CONFIG_FRESH_MS = 60_000;
  let configCache = null; // { config, at }
  let configInFlight = null;

  function layoutMounted() {
    return Boolean(document.getElementById('moon-exchange-sell-locked'));
  }

  function applyConfig(config) {
    if (!config || !layoutMounted()) return;
    if (Number.isFinite(config.feeRate)) feeRate = Math.max(0, config.feeRate);
    if (typeof config.sellUnlocked === 'boolean') {
      sellUnlock = {
        unlocked: config.sellUnlocked,
        finished: Number(config.sellUnlockFinished) || 0,
        required: Number(config.sellUnlockRequired) || 0,
      };
      applySellUnlockState();
    }
    if (Number.isFinite(config.maxListingQuantity)) maxListingQuantity = config.maxListingQuantity;
    if (Number.isFinite(config.maxUnitPrice)) maxUnitPrice = config.maxUnitPrice;
    const configuredDefault = parsePositiveInt(config.defaultUnitPrice);
    const priceInput = document.getElementById('moon-exchange-unit-price');
    if (configuredDefault && !unitPriceEdited && priceInput) {
      priceInput.value = String(configuredDefault);
    }
    if (Number.isFinite(config.defaultPageSize)) pageSize = config.defaultPageSize;
    updateSellerEstimate();
  }

  /* One hop over the raw read, on purpose: open() awaits this on the way to
     the first listings, and the unit harness flushes a fixed handful of
     microtasks around it. */
  function readConfig() {
    if (!configInFlight) {
      const settle = (config) => {
        configInFlight = null;
        if (config) configCache = { config, at: Date.now() };
        return config;
      };
      configInFlight = MoonfallCloud.moonExchange.getConfig().then(settle, (err) => {
        configInFlight = null;
        throw err;
      });
    }
    return configInFlight;
  }

  async function ensureConfig({ maxAgeMs = 0 } = {}) {
    if (!MoonfallCloud?.moonExchange?.getConfig) return;
    if (configCache && Date.now() - configCache.at <= maxAgeMs) {
      applyConfig(configCache.config);
      return;
    }
    try {
      applyConfig(await readConfig());
    } catch (err) {
      console.warn('[MoonExchange] Failed to load config:', err);
    }
  }

  /** Read the config ahead of the tab so the first frame of the Exchange can carry it. */
  function warmConfig() {
    if (!MoonfallCloud?.moonExchange?.getConfig) return Promise.resolve();
    if (configCache && Date.now() - configCache.at <= CONFIG_FRESH_MS) return Promise.resolve();
    return readConfig().then(() => undefined, () => undefined);
  }

  function listingsQueryKey(request) {
    return JSON.stringify({
      sort: request.sort ?? null,
      search: request.search ?? null,
      mineOnly: request.mineOnly ?? null,
      excludeMine: request.excludeMine ?? null,
    });
  }

  /** What every entry shows first: stored sort, all sellers, no search. */
  function entryRequest(limit) {
    return { cursor: null, limit, sort: loadStoredSort() };
  }

  /** Whose listings a read answers: the credential the query is sent with. */
  function listingsViewer() {
    return MoonfallCloud?.getAuthToken?.() || null;
  }

  /** Run a trade mutation; once it settles, every page read before it is owed a re-read. */
  async function trade(run) {
    try {
      return await run();
    } finally {
      tradeRevision += 1;
    }
  }

  /** A page read for `request` that may still be presented (PAGES KEPT), or null. */
  function keptPageFor(request) {
    const key = buildRequestKey(request);
    const kept = keptPages.get(key);
    if (!kept) return null;
    if (
      kept.owner !== listingsViewer()
      || kept.revision !== tradeRevision
      || (kept.settled && Date.now() - kept.at > LISTINGS_WARM_FRESH_MS)
    ) {
      keptPages.delete(key);
      return null;
    }
    return kept;
  }

  function keepPage(request, promise, owner, revision) {
    const key = buildRequestKey(request);
    const kept = { owner, revision, at: 0, settled: false, promise };
    keptPages.delete(key);
    keptPages.set(key, kept);
    while (keptPages.size > PAGES_KEPT) keptPages.delete(keptPages.keys().next().value);
    promise.then(() => {
      kept.settled = true;
      kept.at = Date.now();
    }, () => {
      if (keptPages.get(key) === kept) keptPages.delete(key);
    });
  }

  /**
   * One listings read. A first-page read is shared with an identical one in
   * flight for the same viewer (the warm, the tab's prepare and its open all
   * ask for it) and its answer is remembered for the next entry. Every page
   * read is kept (PAGES KEPT); `useKept` (a page turn) answers with a kept
   * page, or joins its read still on the way, instead of reading again.
   * `notBefore`: a read in flight joins only if it started then or later (an
   * entry's re-read is a read from its own press, never an older one's).
   */
  function readListingsPage(request, { useKept = false, notBefore = 0 } = {}) {
    if (useKept) {
      const kept = keptPageFor(request);
      if (kept) return kept.promise;
    }
    const firstPage = !request.cursor;
    const queryKey = listingsQueryKey(request);
    const owner = listingsViewer();
    if (
      firstPage
      && entryPageRead
      && entryPageRead.queryKey === queryKey
      && entryPageRead.limit === request.limit
      && entryPageRead.owner === owner
      && entryPageRead.startedAt >= notBefore
    ) return entryPageRead.promise;
    const revision = tradeRevision;
    const startedAt = Date.now();
    let promise;
    try {
      promise = Promise.resolve(MoonfallCloud.moonExchange.listOpenListings(request));
    } catch (err) {
      promise = Promise.reject(err);
    }
    if (firstPage) {
      const read = { queryKey, limit: request.limit, owner, promise, startedAt };
      entryPageRead = read;
      entryPageReads.add(read);
      const clear = () => {
        entryPageReads.delete(read);
        if (entryPageRead === read) entryPageRead = null;
      };
      // Registered before any caller awaits this read, so the page is
      // remembered by the time they continue.
      promise.then((response) => {
        // An answer for a viewer who has since signed out is nobody's entry,
        // and one that started before the page remembered is older than it.
        const newer = rememberedEntryPage?.owner === owner
          && rememberedEntryPage.queryKey === queryKey
          && rememberedEntryPage.startedAt > startedAt;
        if (owner === listingsViewer() && !newer) {
          rememberedEntryPage = {
            queryKey, limit: request.limit, owner, tradeRevision: revision, at: Date.now(), startedAt, response,
          };
        }
        clear();
      }, clear);
    }
    keepPage(request, promise, owner, revision);
    return promise;
  }

  /**
   * The remembered first page, cut to this lane's page size — or null when
   * there is none for this query and viewer, it is stale, or it is smaller
   * than the lane. `exact` pages carry their real cursor; a sliced one does
   * not, so its re-read is what enables Next. `traded`: this player traded
   * after it was read.
   */
  function rememberedPageFor(request) {
    const page = rememberedEntryPage;
    if (!page || request.cursor) return null;
    if (page.owner !== listingsViewer()) return null;
    if (page.queryKey !== listingsQueryKey(request)) return null;
    if (Date.now() - page.at > LISTINGS_WARM_FRESH_MS) return null;
    if (!(page.limit >= request.limit)) return null;
    const all = Array.isArray(page.response?.listings) ? page.response.listings : [];
    const exact = page.limit === request.limit;
    return {
      at: page.at,
      startedAt: page.startedAt,
      listings: all.slice(0, request.limit),
      nextCursor: exact ? (page.response?.nextCursor || null) : null,
      isDone: exact
        ? (page.response?.isDone ?? !page.response?.nextCursor)
        : (all.length <= request.limit && page.response?.isDone !== false),
      exact,
      traded: page.tradeRevision !== tradeRevision,
    };
  }

  /** A first-page read in flight that would answer `request` for this viewer. */
  function entryReadCovering(request) {
    const pending = entryPageRead;
    return pending
      && pending.queryKey === listingsQueryKey(request)
      && pending.limit >= request.limit
      && pending.owner === listingsViewer()
      ? pending
      : null;
  }

  /**
   * The remembered page for the entry, waiting on a read already under way:
   * the first of them to land — an older one (the Collection's warm) often
   * lands before the press's own, which then reconciles it (THE MARKET IS
   * READ AGAIN BEHIND A KEPT PAGE).
   */
  async function joinRememberedEntryPage(request) {
    if (rememberedPageFor(request)) return rememberedPageFor(request);
    const pending = [...entryPageReads].filter((read) => (
      read.queryKey === listingsQueryKey(request)
      && read.limit >= request.limit
      && read.owner === listingsViewer()
    ));
    if (pending.length === 0) return null;
    let landed = false;
    await Promise.race(pending.map((read) => read.promise.then(() => {
      landed = true;
    }, () => undefined)));
    // A read that failed first leaves the others to the entry's own read.
    if (!landed && !rememberedPageFor(request)) return null;
    return rememberedPageFor(request);
  }

  /* THE OWNED CATALOG IS READ ON INTENT, NEVER AT THE COLLECTION'S OPEN.
     It is the whole card catalog (the Collection's `/api/cards`: ~750 cards,
     about a second of server work), and it was read on every Collection
     open, pressed or not (read audit R5, 26 September 2026). The owner: "it's
     fine to load things, but not a players entire library. that's why i
     introduced paginations. it's fine to load a few pages ahead". So only
     intent on the tab reads it — a pointer over it, keyboard focus, the first
     touch — or the press, which the Collection makes wait on it. At once:
     the press is a moment away, and the Collection memoises the answer, so
     the press shares this read (or finds it done) and every later intent is
     free. Asked for even when this surface already holds cards: the
     Collection hands it its PAGED grid cache before the catalog exists, and
     that cache rarely holds the listed cards (round 13, 25 September 2026).
     Whatever the entry page holds, too: it once rode the art warm, which
     gives up first on an empty page or without a preloader (review, same
     day), and the press then waited ~1 s for this read. */
  function readCatalog() {
    if (typeof cardsLoader !== 'function') return Promise.resolve(allCards);
    return Promise.resolve().then(cardsLoader).then((cards) => {
      if (Array.isArray(cards) && cards.length > 0) catalogInHand = true;
      return cards;
    }, () => allCards);
  }

  /* How many entry rows the lane shows: its own measure, and before it has
     one (it is hidden until the first press) the most its viewport can hold —
     the lane sits inside the viewport, so that never falls short of it. */
  function entryRowsShown() {
    const viewportFit = calculateListingPageSize({
      availableWidth: window.innerWidth,
      availableHeight: window.innerHeight,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      maxPageSize: pageSize,
    });
    return measuredPageSize > 0 ? Math.min(measuredPageSize, viewportFit) : viewportFit;
  }

  /* The art of the entry rows the lane shows — no more — decoded ahead at
     low priority, on intent, once the owned catalog has answered. Up to a
     whole page of entries is read ahead (24 before the lane is measured);
     decoding every one of them spent megabytes on rows below the pager. (The
     bytes are warmed earlier, from the rows' own art: warmEntryRowsArt.) */
  function warmEntryArt(response, cards) {
    if (typeof AssetPreloader?.warmCards !== 'function') return undefined;
    const shown = (Array.isArray(response?.listings) ? response.listings : []).slice(0, entryRowsShown());
    const byId = new Map((Array.isArray(cards) ? cards : []).map((card) => [card.id, card]));
    const wanted = [];
    shown.forEach((entry) => {
      const card = byId.get(entry.cardId);
      if (card && !wanted.includes(card)) wanted.push(card);
    });
    if (wanted.length === 0) return undefined;
    return AssetPreloader.warmCards(wanted, { priority: 'low' });
  }

  /* The cards behind the entry rows that do not carry their own art (an
     older row: `listOpenListings` rows carry `cardImage` since read audit
     F3) — or behind every entry row with `forFaces`, when the tab opens
     before the catalog (their faces need the cards' localized words) — from
     page-scoped card data: the cards this surface already holds, and the
     rest from the details route (at most the rows the lane shows, in the
     player's language, kept for the faces) — never the owned catalog. []
     when there is nothing to read, or when unreadable. The cards found among
     this surface's own are kept for the faces too: the Collection hands its
     paged cards over first and empties them at the press (until the catalog
     lands), and rows whose cards had been found there then waited for the
     catalog (round 18: ~0.9 s on the phone's market pane, offline). */
  async function readEntryCards(response, { forFaces = false } = {}) {
    const shown = (Array.isArray(response?.listings) ? response.listings : []).slice(0, entryRowsShown());
    const ids = [...new Set(shown
      .filter((entry) => forFaces || !rowOwnArt(entry))
      .map((entry) => entry?.cardId)
      .filter(Boolean))];
    if (ids.length === 0) return [];
    const byId = readerLanguageCardsById();
    if (forFaces) keepPageCards(ids.map((id) => byId.get(id)).filter(Boolean));
    const missing = ids.filter((id) => !byId.has(id));
    if (missing.length > 0 && typeof fetch === 'function') {
      try {
        const token = MoonfallCloud?.getAuthToken?.() || null;
        const params = new URLSearchParams({ cardIds: missing.join(','), lang: i18n?.getLanguage?.() || 'en' });
        const reply = await fetch(`/api/cards/details?${params.toString()}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const details = reply?.ok ? await reply.json() : [];
        keepPageCards(details);
        (Array.isArray(details) ? details : []).forEach((card) => {
          if (card?.id && !byId.has(card.id)) byId.set(card.id, card);
        });
      } catch (_) {
        // A warm that cannot read the details warms what it has.
      }
    }
    return ids.map((id) => byId.get(id)).filter(Boolean);
  }

  /* ROWS' ART AHEAD, IN SCOPES OF THIS SURFACE'S OWN. The entry rows' art
     (warmed at the Collection's open) and the page ahead's (warmed once a
     page is on screen) wait in the one scheduler's background, behind the
     Collection's own next page. A row asks for its picture itself (Cards,
     outside the scheduler), so intent on the tab and the press RAISE this
     surface's pictures to the foreground (`promote`): a download already
     running is joined at the new priority, and one still queued starts now
     — the rows find it on the wire instead of asking for it anew, behind the
     owned catalog's read. Round 17 (28 September 2026, WebKit 4G phone): a
     warm left queued behind the Collection's look-ahead started nothing
     before a quick press, and the entry then waited for the catalog and for
     its art. Dropped with the surface (close). */
  let entryArt = null; // { scope, urls }
  // Intent or the press came before the entry rows' art was known.
  let entryArtWanted = false;
  let pageAheadArt = null; // { key, scope, urls }

  function artUrlOf(card) {
    const source = normalizeExchangeImageSource(card?.imageUrl || card?.image || '');
    // A generated raster is already in memory: nothing to download.
    return source && !/^(data|blob):/i.test(source) ? source : '';
  }

  /* A row's own picture: the URL the collection serves for its card, which
     `listOpenListings` rows carry since read audit F3 (28 September 2026:
     equal to the catalogue's for every open listing on dev, in English and
     Japanese). '' for an older row. */
  function rowOwnArt(listing) {
    return typeof listing?.cardImage === 'string' ? artUrlOf({ image: listing.cardImage }) : '';
  }

  function pageCardsKey() {
    return `${listingsViewer() || ''}|${i18n?.getLanguage?.() || 'en'}`;
  }

  /** Keep page-scoped cards (the details route's answer) for the rows' faces. */
  function keepPageCards(cards) {
    const key = pageCardsKey();
    if (pageCards.key !== key) pageCards = { key, byId: new Map() };
    (Array.isArray(cards) ? cards : []).forEach((card) => {
      if (card?.id) pageCards.byId.set(card.id, card);
    });
  }

  /** Whether the catalog in hand is in the reader's language. */
  function catalogInReaderLanguage() {
    return catalogLanguage === null || catalogLanguage === readerLanguage();
  }

  /** The cards this surface holds in the reader's language: the page's own, with the catalog's over them. */
  function readerLanguageCardsById() {
    const byId = new Map(pageCards.key === pageCardsKey() ? pageCards.byId : []);
    if (!catalogInReaderLanguage()) return byId;
    (Array.isArray(allCards) ? allCards : []).forEach((card) => {
      if (card?.id) byId.set(card.id, card);
    });
    return byId;
  }

  /* The cards rows are drawn from: those in the reader's language, and a
     catalog in another language only where there is none (its words are
     still the player's own language of a moment ago, never the listing's
     English snapshot). */
  function faceCardsById() {
    const byId = readerLanguageCardsById();
    if (catalogInReaderLanguage()) return byId;
    (Array.isArray(allCards) ? allCards : []).forEach((card) => {
      if (card?.id && !byId.has(card.id)) byId.set(card.id, card);
    });
    return byId;
  }

  /** Whether any of these listings has no card in the reader's language to draw its face from. */
  function listingsMissFaces(rows) {
    const byId = readerLanguageCardsById();
    return (Array.isArray(rows) ? rows : []).some((entry) => entry?.cardId && !byId.has(entry.cardId));
  }

  /* Resolves when the catalog lands (setContext), or after `capMs`; at once
     when none is on its way. */
  function catalogLandedWithin(capMs) {
    if (!catalogPending) return Promise.resolve();
    if (!catalogLanded) {
      let resolveLanded = () => {};
      const promise = new Promise((done) => { resolveLanded = () => done(undefined); });
      catalogLanded = { promise, resolve: resolveLanded };
    }
    return Promise.race([
      catalogLanded.promise,
      new Promise((resolve) => {
        setTimeout(resolve, capMs);
      }),
    ]);
  }

  /* The card a row's face is drawn from: the catalogue's — its words are
     localized and current, where a row's `cardName`/`cardDescription` are
     the listing's English snapshot — with the row's own picture, the URL the
     warms ahead fetched. null when this surface does not hold the card (the
     row then shows its own picture alone). */
  function faceCardFor(entry, cardById) {
    const card = cardById.get(entry?.cardId) || null;
    if (!card) return null;
    const own = rowOwnArt(entry);
    return own && own !== artUrlOf(card) ? { ...card, image: own } : card;
  }

  /** The art of the first `rows` rows of a listings answer: each row's own, else its card's from `cards`. */
  function rowsArtUrls(response, cards, rows) {
    const shown = (Array.isArray(response?.listings) ? response.listings : []).slice(0, rows);
    const byId = new Map((Array.isArray(cards) ? cards : []).map((card) => [card?.id, card]));
    return [...new Set(shown.map((entry) => rowOwnArt(entry) || artUrlOf(byId.get(entry?.cardId))).filter(Boolean))];
  }

  function artScope(name, options = {}) {
    if (typeof LoadScheduler?.scope !== 'function') return null;
    // Not under Save-Data or on a link the browser rates slow.
    return LoadScheduler.scope(name, { ...options, admit: (policy) => !policy?.constrained });
  }

  /* The entry rows' art, ahead of the tab (the Collection's open). */
  function warmEntryRowsArt(response, cards) {
    const urls = rowsArtUrls(response, cards, entryRowsShown());
    if (urls.length === 0) return undefined;
    entryArt?.scope.close();
    entryArt = null;
    const scope = artScope('moon-exchange-entry');
    if (!scope) return warmEntryArt(response, cards);
    entryArt = { scope, urls };
    const warming = scope.soon(urls);
    if (entryArtWanted) raiseEntryRowsArt();
    return warming;
  }

  /* Intent on the tab, or its press: the entry rows' art now, at the foreground. */
  function raiseEntryRowsArt() {
    if (entryArt && !entryArt.scope.closed) entryArt.scope.promote(entryArt.urls, 'foreground');
    else entryArtWanted = true;
  }

  function dropRowsArt() {
    entryArtWanted = false;
    entryArt?.scope.close();
    entryArt = null;
    pageAheadArt?.scope.close();
    pageAheadArt = null;
  }

  /**
   * Warm the Moon Exchange tab ahead of the press. Best-effort
   * (docs/asset-loading.md law 3): nothing waits on it, failures are
   * swallowed, and the open still re-reads.
   *   - The Collection's open: the config and the entry page — one page —
   *     and, once that page answers, the art of the entry rows the lane
   *     shows, from page-scoped card data (readEntryCards), at low priority
   *     behind the Collection's own pictures. Round 17 (28 September 2026):
   *     the entry waited for the owned catalog AND then downloaded its rows'
   *     art (catalog 0.9 s, then art 1.8 s on the WebKit 4G phone); with the
   *     art in hand the entry waits for the catalog alone.
   *   - Intent on the tab (`intent`): also the owned catalog, at once, and
   *     then the art of the entry rows the lane shows.
   *
   * @param {object} [options]
   * @param {(() => Promise<unknown[]>)|null} [options.loadCards] the owned
   *   catalog's loader (the Collection's), kept for intent: the read the tab's
   *   entry waits on, and the entry rows' art
   * @param {boolean} [options.intent] the player pointed at, focused or
   *   touched the tab
   * @param {boolean} [options.withoutCatalog] the Collection opens the tab
   *   before its catalog has answered: the entry rows' cards are read with
   *   the page, for their faces
   */
  function warm({ loadCards = null, intent = false, withoutCatalog = false } = {}) {
    if (typeof loadCards === 'function') cardsLoader = loadCards;
    if (withoutCatalog === true) facesReadAhead = true;
    const config = warmConfig();
    if (isOpen || typeof MoonfallCloud?.moonExchange?.listOpenListings !== 'function') return config;
    const limit = measuredPageSize || Math.max(1, Math.floor(Number(pageSize) || 1));
    const request = entryRequest(limit);
    const page = rememberedPageFor(request)
      ? Promise.resolve(rememberedEntryPage.response)
      : readListingsPage(request);
    // Intent with no loader yet waits for the Collection's hand-over.
    const intended = intent || catalogIntentPending;
    catalogIntentPending = intended && typeof cardsLoader !== 'function';
    if (intent) raiseEntryRowsArt();
    if (!intended || catalogIntentPending) {
      // Rows carrying their own art read no card for it; a tab opened before
      // the catalog reads its entry rows' cards for their faces.
      const art = page.then((response) => readEntryCards(response, { forFaces: facesReadAhead })
        .then((cards) => warmEntryRowsArt(response, cards)));
      return Promise.all([config, page, art]).then(() => undefined, () => undefined);
    }
    // Read beside the page, not behind it: a failed page still leaves the
    // catalog the tab's entry waits on.
    const catalog = readCatalog();
    const art = Promise.all([page, catalog]).then(([response, cards]) => warmEntryArt(response, cards));
    return Promise.all([config, catalog, art]).then(() => undefined, () => undefined);
  }

  /* A WARM ENTRY has had its reads answered: the entry page was read this
     session (its re-read, when one is due, is a round trip) and the owned
     catalog is in hand (intent on the tab, or an earlier entry), so the
     Collection hands the tab over at once. It
     commits after a few hundred milliseconds of raster work — inside the
     shared grace, so it waits the grace out and shows no loading state at
     all, where the few-frames state only flashed in front of the rows (round
     13: "2 placeholder(s) on a warm cache" on every warm and revisit visit).
     Any other entry has a real wait ahead and shows its one state after a few
     frames; there it may be the only visible answer to the press.
     Opened before the catalog, it is warm with the cards of the rows the lane
     shows in hand — the rows the warm read them for. It was judged by every
     row of the page read ahead (up to a whole unmeasured page), which the
     warm never reads, so no entry was ever warm and every one showed its
     loading state after a few frames (round 18, the meter's collection
     journey: 0.14-0.36 s on every warm and revisit entry). */
  function entryIsWarm() {
    const remembered = rememberedEntryPage?.queryKey === listingsQueryKey(entryRequest(0))
      && rememberedEntryPage.owner === listingsViewer();
    if (!remembered) return false;
    const rows = Array.isArray(rememberedEntryPage.response?.listings) ? rememberedEntryPage.response.listings : [];
    return catalogInHand || (facesReadAhead && !listingsMissFaces(rows.slice(0, entryRowsShown())));
  }

  function beginEntryHold() {
    if (entryHold?.active) return;
    entryHold = SurfaceEntryHold.hold(getSection(), {
      label: () => uiText('collection.exchange.loadingListings', 'Loading listings...'),
      loadingDelayMs: entryIsWarm()
        ? SurfaceEntryHold.LOADING_GRACE_MS
        : SurfaceEntryHold.LOADING_STATE_DELAY_MS,
    });
  }

  function releaseEntryHold(reason = 'ready') {
    const hold = entryHold;
    entryHold = null;
    hold?.release(reason);
  }

  /**
   * The lane's fade-in is an ENTRANCE, played once when rows first replace
   * nothing (skeletons, an empty lane, the first entry). It rode the
   * `me-loaded` state class, which stays on: every return to the tab shows a
   * display:none section again, which restarts its CSS animations, and the
   * rows the player had just seen faded back in from transparent. The class
   * is spent once played (a backstop for engines that report no end).
   */
  function playLaneEntrance(container) {
    container.classList.add('me-listings-revealing');
    let spent = false;
    const spend = () => {
      if (spent) return;
      spent = true;
      container.removeEventListener('animationend', onEnd);
      container.classList.remove('me-listings-revealing');
    };
    const onEnd = (event) => {
      if (event.target === container) spend();
    };
    container.addEventListener('animationend', onEnd);
    setTimeout(spend, LANE_ENTRANCE_BACKSTOP_MS);
  }

  function laneHasCommittedContent() {
    const container = document.getElementById('moon-exchange-listings');
    return Boolean(container?.dataset.renderSignature) && container.childElementCount > 0;
  }

  /**
   * Build the layout and apply the warmed answer — synchronously, before the
   * section is shown — and hold the section until open() has committed its
   * first page (surface-entry-hold.js): the tab then appears in one frame.
   * The entry reads start here, right after the section is displayed, instead
   * of behind the Collection's catalog read.
   */
  function prepare() {
    // The press: the Collection reads the catalog for it from here.
    catalogIntentPending = false;
    // The market comes up under wherever the pointer rests.
    hoverIntent.disarm();
    // And its rows' art starts now, beside the catalog, not behind it.
    raiseEntryRowsArt();
    ensureLayout();
    if (configCache) applyConfig(configCache.config);
    beginEntryHold();
    entryPressedAt = Date.now();
    /* The entry page is read at the press, even when one is remembered: a
       remembered page is presented at once and read again behind the view
       (THE MARKET IS READ AGAIN BEHIND A KEPT PAGE), and this read, joined,
       is that re-read, started as early as the press. */
    const pressedAt = entryPressedAt;
    const startEntryReads = () => {
      if (!entryHold?.active || typeof MoonfallCloud?.moonExchange?.listOpenListings !== 'function') return;
      syncListingsPageSize();
      void warmConfig();
      const request = entryRequest(activePageSize);
      const covering = entryReadCovering(request);
      if (!covering || !(covering.startedAt >= pressedAt)) {
        void readListingsPage(request, { notBefore: pressedAt }).catch(() => undefined);
      }
    };
    if (typeof queueMicrotask === 'function') queueMicrotask(startEntryReads);
    else void Promise.resolve().then(startEntryReads);
  }

  function updatePaginationControls() {
    const prevBtn = document.getElementById('moon-exchange-prev');
    const nextBtn = document.getElementById('moon-exchange-next');
    const pageInfo = document.getElementById('moon-exchange-page-info');
    const pageIndex = cursorStack.length + 1;
    if (pageInfo) {
      pageInfo.textContent = uiText('common.page', `Page ${pageIndex}`, {
        page: pageIndex,
      });
    }
    if (prevBtn) prevBtn.disabled = cursorStack.length === 0 || isLoading;
    if (nextBtn) nextBtn.disabled = !nextCursor || isDone || isLoading;
  }

  function measureListingsPageSize() {
    const container = document.getElementById('moon-exchange-listings');
    const bounds = container?.getBoundingClientRect?.();
    const availableWidth = Number(container?.clientWidth) > 0
      ? Number(container.clientWidth)
      : Number(bounds?.width);
    const availableHeight = Number(container?.clientHeight) > 0
      ? Number(container.clientHeight)
      : Number(bounds?.height);
    const maximum = Math.max(1, Math.floor(Number(pageSize) || 1));
    if (!(availableWidth > 0) || !(availableHeight > 0)) {
      return Math.min(activePageSize, maximum);
    }
    // Remembered for the warm, which runs while the lane is hidden.
    measuredPageSize = calculateListingPageSize({
      availableWidth,
      availableHeight,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      maxPageSize: pageSize,
    });
    return measuredPageSize;
  }

  function syncListingsPageSize() {
    const nextPageSize = measureListingsPageSize();
    const changed = nextPageSize !== activePageSize;
    activePageSize = nextPageSize;
    const container = document.getElementById('moon-exchange-listings');
    if (container) container.dataset.pageSize = String(activePageSize);
    return changed;
  }

  function getViewportRenderSignature() {
    return [
      Number(window.innerWidth) || 0,
      Number(window.innerHeight) || 0,
      Number(window.devicePixelRatio) || 1,
    ].join('x');
  }

  /* A finger is on the glass.
     The page size is measured from the live viewport, and on iOS the URL bar
     collapses DURING a scroll gesture — so the viewport grows mid-swipe, a
     different number of listings fits, and the board re-flows under the
     player's finger instead of moving. Reported as "very brittle from
     scrolling: it doesn't scroll but reorganises the board to have fewer
     rows". A viewport change that arrives while a touch is in flight is the
     browser's own chrome, not a real layout change, so it is deferred until
     the gesture ends rather than acted on. */
  let touchInFlight = 0;
  let deferredViewportChange = false;

  function beginTouchGesture() {
    touchInFlight += 1;
  }

  function endTouchGesture() {
    touchInFlight = Math.max(0, touchInFlight - 1);
    if (touchInFlight === 0 && deferredViewportChange) {
      deferredViewportChange = false;
      handleViewportChange();
    }
  }

  function handleViewportChange() {
    if (!isOpen || isStaleInstance()) return;
    if (touchInFlight > 0) {
      deferredViewportChange = true;
      return;
    }
    syncCommandBarPlacement();
    const nextViewportSignature = getViewportRenderSignature();
    const viewportChanged = nextViewportSignature !== viewportRenderSignature;
    viewportRenderSignature = nextViewportSignature;
    if (syncListingsPageSize() || viewportChanged) {
      void refreshListings({ reset: true });
    }
  }

  function scheduleLayoutSync() {
    if (!isOpen || isStaleInstance()) return;
    const cancel = window.cancelAnimationFrame || window.clearTimeout;
    const schedule = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 0));
    if (layoutFrame !== null) cancel(layoutFrame);
    layoutFrame = schedule(() => {
      layoutFrame = null;
      handleViewportChange();
    });
  }

  function observeListingLayout() {
    layoutResizeObserver?.disconnect?.();
    layoutResizeObserver = null;
    const container = document.getElementById('moon-exchange-listings');
    if (!container || typeof ResizeObserver !== 'function') return;
    layoutResizeObserver = new ResizeObserver(scheduleLayoutSync);
    layoutResizeObserver.observe(container);
  }

  function stopObservingListingLayout() {
    layoutResizeObserver?.disconnect?.();
    layoutResizeObserver = null;
    if (layoutFrame !== null) {
      const cancel = window.cancelAnimationFrame || window.clearTimeout;
      cancel(layoutFrame);
    }
    layoutFrame = null;
  }

  function buildRequestKey(request) {
    return JSON.stringify({
      cursor: request.cursor ?? null,
      limit: request.limit ?? null,
      sort: request.sort ?? null,
      search: request.search ?? null,
      mineOnly: request.mineOnly ?? null,
      excludeMine: request.excludeMine ?? null,
    });
  }

  function getBackoffDelay(baseDelay, maxDelay, attempt) {
    const step = Math.max(1, attempt);
    return Math.min(baseDelay * Math.pow(2, step - 1), maxDelay);
  }

  function clearSearchBackfillPoll() {
    if (searchBackfillTimer) {
      clearTimeout(searchBackfillTimer);
      searchBackfillTimer = null;
    }
    searchBackfillAttempts = 0;
  }

  function scheduleSearchBackfillPoll() {
    if (searchBackfillTimer || searchBackfillAttempts >= MAX_SEARCH_BACKFILL_POLL_ATTEMPTS) return;
    searchBackfillAttempts += 1;
    const delay = getBackoffDelay(
      SEARCH_BACKFILL_POLL_INTERVAL_MS,
      SEARCH_BACKFILL_POLL_MAX_INTERVAL_MS,
      searchBackfillAttempts
    );
    searchBackfillTimer = setTimeout(async () => {
      searchBackfillTimer = null;
      await ensureSearchTextBackfill();
      if (searchBackfillStatus === 'backfilling' && searchText.trim()) {
        scheduleSearchBackfillPoll();
      }
    }, delay);
  }


  async function ensureSearchTextBackfill() {
    if (!MoonfallCloud?.moonExchange?.ensureSearchTextBackfill) return;
    if (searchBackfillStatus === 'ready') return;
    if (searchBackfillStatus === 'backfilling') {
      if (searchText.trim()) {
        scheduleSearchBackfillPoll();
      }
      return;
    }
    try {
      const previousStatus = searchBackfillStatus;
      const result = await MoonfallCloud.moonExchange.ensureSearchTextBackfill();
      if (result?.status) searchBackfillStatus = result.status;
      if (searchBackfillStatus === 'ready') {
        clearSearchBackfillPoll();
        if (previousStatus === 'backfilling' && searchText.trim()) {
          refreshListings({ reset: true });
        }
      } else if (searchBackfillStatus === 'backfilling') {
        if (searchText.trim()) {
          scheduleSearchBackfillPoll();
        }
      }
    } catch (err) {
      console.warn('[MoonExchange] Failed to backfill search text:', err);
      searchBackfillStatus = null;
    }
  }

  /** The listings request for the page at `cursor`, under the lane's current query. */
  function listingsRequest(cursor) {
    const request = {
      cursor,
      limit: activePageSize,
      sort: currentSort,
    };
    const search = String(searchText || '').trim();
    if (search) {
      request.search = search;
    }
    if (currentOwnerFilter === 'mine') {
      request.mineOnly = true;
    } else if (currentOwnerFilter === 'others') {
      request.excludeMine = true;
    }
    return request;
  }

  /* ONE PAGE AHEAD (PAGES KEPT): once the page on screen is committed with
     its pictures in, the next page is read and its rows' art warmed in a
     scope of this surface's own (ROWS' ART AHEAD). */
  function readPageAhead() {
    if (!isOpen || isStaleInstance() || isLoading || isDone || !nextCursor) return;
    if (typeof MoonfallCloud?.moonExchange?.listOpenListings !== 'function') return;
    const request = listingsRequest(nextCursor);
    if (keptPageFor(request)) return;
    const key = buildRequestKey(request);
    void readListingsPage(request).then((response) => {
      if (!isOpen || isStaleInstance()) return;
      const urls = rowsArtUrls(response, allCards, activePageSize);
      pageAheadArt?.scope.close();
      pageAheadArt = null;
      if (urls.length === 0) return;
      /* The screen's own next view: STEADY (load-scheduler.ts), two at a
         time, yielding only to a picture the screen is loading — the plain
         background waits (a quiet after every press and data read, the
         global two-download limit) left it unfinished when Next came a
         second or two later. Ranked ahead of other surfaces' work. */
      const scope = artScope('moon-exchange-ahead', { steady: true, maxInFlight: 2 });
      if (!scope) {
        void warmEntryArt(response, allCards);
        return;
      }
      pageAheadArt = { key, scope, urls };
      void scope.soon(urls.map((url) => ({ url, rank: -1 })));
    }, () => undefined);
  }

  /* A turn to the page read ahead raises its rows' art to the foreground
     before the rows ask for it (ROWS' ART AHEAD). */
  function raisePageAheadArt(request) {
    if (pageAheadArt && !pageAheadArt.scope.closed && pageAheadArt.key === buildRequestKey(request)) {
      pageAheadArt.scope.promote(pageAheadArt.urls, 'foreground');
    }
  }

  /**
   * @param {object} [options]
   * @param {boolean} [options.reset] back to the first page
   * @param {Promise<unknown>|null} [options.beforeRender] an answer the rows
   *   print (the entry's config: the buy lock) — read in parallel, awaited
   *   before the page is built
   * @param {(() => void)|null} [options.onData] the page has answered
   * @param {boolean} [options.useKept] a page turn: a page kept from a read
   *   within the window answers it (PAGES KEPT)
   */
  async function refreshListings({ reset = false, beforeRender = null, onData = null, useKept = false } = {}) {
    if (!MoonfallCloud?.moonExchange?.listOpenListings) return;
    if (isStaleInstance()) return;

    if (reset) {
      syncListingsPageSize();
      currentCursor = null;
      nextCursor = null;
      cursorStack = [];
      isDone = false;
      updatePaginationControls();
    }

    const request = listingsRequest(currentCursor);
    const requestKey = buildRequestKey(request);

    if (isLoading) {
      if (requestKey === activeRequestKey || requestKey === pendingRefreshKey) {
        return;
      }
      pendingRefresh = true;
      pendingRefreshKey = requestKey;
      if (reset) pendingRefreshReset = true;
      return;
    }

    isLoading = true;
    listingsRenderGeneration += 1;
    document.querySelector('#moon-exchange-listings > .moon-exchange-paint-stage')?.remove();
    listingsLoadError = null;
    activeRequestKey = requestKey;
    document.getElementById('moon-exchange-listings')?.setAttribute('aria-busy', 'true');
    // A page kept from an earlier read (settled) is shown at once, and read again.
    const shownFromKept = Boolean(useKept && keptPageFor(request)?.settled);
    try {
      const response = await readListingsPage(request, { useKept });
      listings = response?.listings || [];
      listingsLoadError = null;
      nextCursor = response?.nextCursor || null;
      isDone = response?.isDone ?? !nextCursor;
      const listingSnapshot = listings;
      loadListingStellarAssets(listingSnapshot).then((assetsLoaded) => {
        if (!assetsLoaded || !isOpen || isStaleInstance() || listings !== listingSnapshot) return;
        hydrateListingStellarPreviews(listingSnapshot);
      });
    } catch (err) {
      console.warn('[MoonExchange] Failed to load listings:', err);
      listings = [];
      nextCursor = null;
      isDone = true;
      listingsLoadError = uiText('collection.exchange.loadListingsFailed', 'Listings are unavailable.');
    } finally {
      isLoading = false;
      if (beforeRender) await beforeRender;
      if (typeof onData === 'function') onData();
      const committed = await renderListings();
      updatePaginationControls();
      if (pendingRefresh) {
        const nextReset = pendingRefreshReset;
        pendingRefresh = false;
        pendingRefreshReset = false;
        pendingRefreshKey = null;
        refreshListings({ reset: nextReset });
      } else if (committed) {
        readPageAhead();
        if (shownFromKept) revalidateShownPage();
      }
    }
  }

  /* THE MARKET IS READ AGAIN BEHIND A KEPT PAGE (round 18b, 29 September
     2026). The listings are every player's, and change under a page kept
     from an earlier read (the owner: "If something changes, you should
     update the preloaded things"). A remembered entry page, or a kept page
     a turn lands on, was presented within LISTINGS_WARM_FRESH_MS with no
     read at all, up to 30 s stale; `exchange.real.spec.js`, which swaps the
     market for a fixture and enters again, never saw the fixture. It is
     still presented at once, and then read again behind the view: the
     answer is reconciled into the page it was read for, if that page is
     still the one on screen — a row that prints the same is kept (A ROW
     THAT PRINTS THE SAME IS KEPT), so an unchanged market changes nothing.
     A read that fails leaves the page as it is: it is what the market was a
     moment ago, where the error line would take it away. */
  function revalidateShownPage(notBefore = Date.now()) {
    if (!isOpen || isStaleInstance() || isLoading) return;
    if (typeof MoonfallCloud?.moonExchange?.listOpenListings !== 'function') return;
    const request = listingsRequest(currentCursor);
    const key = buildRequestKey(request);
    const shown = listings;
    // A read already on its way joins only if it started for this showing.
    void readListingsPage(request, { notBefore }).then((response) => {
      if (!isOpen || isStaleInstance() || isLoading || listings !== shown) return;
      if (buildRequestKey(listingsRequest(currentCursor)) !== key) return;
      listings = Array.isArray(response?.listings) ? response.listings : [];
      nextCursor = response?.nextCursor || null;
      isDone = response?.isDone ?? !nextCursor;
      listingsLoadError = null;
      const listingSnapshot = listings;
      loadListingStellarAssets(listingSnapshot).then((assetsLoaded) => {
        if (!assetsLoaded || !isOpen || isStaleInstance() || listings !== listingSnapshot) return;
        hydrateListingStellarPreviews(listingSnapshot);
      });
      void renderListings().then((committed) => {
        updatePaginationControls();
        if (committed) readPageAhead();
      });
    }, (err) => {
      console.warn('[MoonExchange] The page on screen could not be read again; it stays:', err);
    });
  }

  function parsePositiveInt(value) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) return null;
    return Math.floor(numeric);
  }

  function normalizeListingQuantity(listing, value) {
    const quantity = parsePositiveInt(value);
    if (!quantity) return null;
    const remaining = parsePositiveInt(listing?.quantityRemaining);
    return remaining && quantity > remaining ? remaining : quantity;
  }

  function getListingTotal(listing, quantity) {
    const unitPrice = Number(listing?.unitPrice);
    const qty = Number(quantity);
    if (!Number.isFinite(unitPrice) || unitPrice <= 0 || !Number.isFinite(qty) || qty <= 0) {
      return null;
    }
    return unitPrice * Math.floor(qty);
  }

  function canAffordListing(listing, quantity) {
    const total = getListingTotal(listing, quantity);
    const currentStardust = Number(stardust);
    return total !== null && Number.isFinite(currentStardust) && currentStardust >= total;
  }

  function isNotEnoughStardustError(err) {
    return /not enough stardust/i.test(String(err?.message || err || ''));
  }

  function getPurchaseErrorMessage(err) {
    if (isNotEnoughStardustError(err)) return getNotEnoughStardustMessage();
    return err?.message || uiText('collection.exchange.purchaseFailed', 'Purchase failed.');
  }

  function syncListingAffordability(listingEl) {
    if (!listingEl) return;
    const listingId = listingEl.dataset?.listingId;
    const listing = listings.find((entry) => entry.id === listingId);
    const buyBtn = listingEl.querySelector('.moon-exchange-buy-btn');
    const qtyInput = listingEl.querySelector('.moon-exchange-buy-qty');
    if (!listing || !buyBtn || !qtyInput) return;

    const quantity = normalizeListingQuantity(listing, qtyInput.value) || 1;
    if (qtyInput.value !== String(quantity)) {
      qtyInput.value = String(quantity);
    }
    const affordable = canAffordListing(listing, quantity);
    buyBtn.disabled = !affordable;
    buyBtn.title = affordable ? '' : getNotEnoughStardustMessage();
    buyBtn.setAttribute('aria-disabled', affordable ? 'false' : 'true');
  }

  function handleNextPage() {
    if (!nextCursor || isLoading) return;
    cursorStack.push(currentCursor);
    currentCursor = nextCursor;
    raisePageAheadArt(listingsRequest(currentCursor));
    refreshListings({ useKept: true });
  }

  function handlePrevPage() {
    if (cursorStack.length === 0 || isLoading) return;
    currentCursor = cursorStack.pop() || null;
    refreshListings({ useKept: true });
  }

  function handleSearchChange(value) {
    searchText = String(value || '');
    if (searchText.trim()) {
      ensureSearchTextBackfill();
    } else {
      searchBackfillStatus = null;
      clearSearchBackfillPoll();
    }
    refreshListings({ reset: true });
  }

  function handleSortChange(value) {
    if (!value) return;
    currentSort = normalizeSort(value);
    persistSort(currentSort);
    refreshListings({ reset: true });
  }

  function handleOwnerFilterChange(value) {
    currentOwnerFilter = normalizeOwnerFilter(value);
    refreshListings({ reset: true });
  }

  async function handleCreateListing() {
    if (!MoonfallCloud?.moonExchange?.createListing) return;

    const select = document.getElementById('moon-exchange-card-select');
    const priceInput = document.getElementById('moon-exchange-unit-price');
    const qtyInput = document.getElementById('moon-exchange-quantity');
    const createBtn = document.getElementById('moon-exchange-create-btn');
    if (!select || !priceInput || !qtyInput || !createBtn) return;

    const { cardId, variant } = parseCardKey(select.value);
    const isStellar = variant === 'stellar';
    const unitPrice = parsePositiveInt(priceInput.value);
    const quantity = parsePositiveInt(qtyInput.value);
    const available = getOwnedQty(cardId, variant);

    if (!cardId) {
      setStatus(uiText('collection.exchange.selectCardToList', 'Select a card to list.'), 'error');
      return;
    }
    if (!unitPrice) {
      setStatus(uiText('collection.exchange.validUnitPrice', 'Enter a valid unit price.'), 'error');
      return;
    }
    if (!quantity) {
      setStatus(uiText('collection.exchange.validQuantity', 'Enter a valid quantity.'), 'error');
      return;
    }
    if (maxUnitPrice && unitPrice > maxUnitPrice) {
      setStatus(uiText('collection.exchange.maxUnitPrice', 'Unit price must be <= {{max}}.', {
        max: maxUnitPrice,
      }), 'error');
      return;
    }
    if (maxListingQuantity && quantity > maxListingQuantity) {
      setStatus(uiText('collection.exchange.maxQuantity', 'Quantity must be <= {{max}}.', {
        max: maxListingQuantity,
      }), 'error');
      return;
    }
    if (quantity > available) {
      setStatus(uiText('collection.exchange.notEnoughPrints', 'Not enough prints to list.'), 'error');
      return;
    }

    createBtn.disabled = true;
    setStatus(uiText('collection.exchange.creatingListing', 'Creating listing...'));

    try {
      await trade(() => MoonfallCloud.moonExchange.createListing(cardId, unitPrice, quantity, isStellar));
      applyInventoryDelta(cardId, variant, -quantity);
      await refreshSellableCards();
      renderSellOptions();
      setStatus(uiText('collection.exchange.createdListing', 'Listing created. Prints moved to escrow.'), 'success');
      // Where the wait for a buyer starts: one line offering a Discord ping.
      const offer = DiscordPings?.offerFor?.('sales');
      if (offer) document.getElementById('moon-exchange-status')?.append(offer);
      await refreshListings({ reset: true });
    } catch (err) {
      console.error('[MoonExchange] Failed to create listing:', err);
      const locked = readExchangeLockedError(err);
      if (locked) {
        setStatus(exchangeLockedMessage(locked), 'error');
      } else {
        setStatus(playerFacingError(err,
        uiText('collection.exchange.createFailed', 'Failed to create listing.')), 'error');
      }
    } finally {
      createBtn.disabled = false;
    }
  }

  function updateListingTotal(input) {
    const listingEl = input.closest('.moon-exchange-listing');
    if (!listingEl) return;
    const unitPrice = Number(listingEl.dataset.unitPrice || 0);
    const listing = listings.find((entry) => entry.id === listingEl.dataset?.listingId);
    const max = parsePositiveInt(input.max || listing?.quantityRemaining);
    let qty = normalizeListingQuantity(listing, input.value) || 1;
    if (max && qty > max) qty = max;
    input.value = String(qty);

    const totalEl = listingEl.querySelector('.moon-exchange-buy-total');
    if (totalEl) {
      renderStardustText(
        totalEl,
        'collection.exchange.total',
        'Total: {{total}} Stardust',
        unitPrice * qty,
        'total',
      );
    }
    syncListingAffordability(listingEl);
  }

  /**
   * Everything the GRID has to do once the server accepts a purchase.
   *
   * Shared by the row's own Buy button and the listing view's, because those
   * two are the same purchase and must not be able to drift: the inventory
   * delta, the balance, the achievement drain and the two refreshes are the
   * grid's bookkeeping, and the server is authoritative for everything above
   * them.
   */
  async function applyPurchaseResult(listing, quantity, result) {
    const filled = result?.quantityFilled || quantity;
    const variant = listing.isStellar ? 'stellar' : 'normal';
    applyInventoryDelta(listing.cardId, variant, filled);

    if (Number.isFinite(result?.buyerStardust)) {
      stardust = result.buyerStardust;
      if (typeof onStardustUpdate === 'function') {
        onStardustUpdate(stardust);
      }
    }

    // Two independent reasons a banner may be waiting, and the Exchange
    // Ledger cardback used to ride on the recipe one — so an ordinary
    // first trade unlocked it in silence. One drain covers both.
    if (['created', 'repaired'].includes(result?.recipeDiscovery?.status)
        || result?.cardbackUnlocked) {
      void getCloudAchievementBannerConsumer()?.();
    }

    await refreshSellableCards({ forceRemote: true });
    await refreshListings();
  }

  /** The same, for a cancellation. */
  async function applyCancelResult(listing, result) {
    const variant = listing.isStellar ? 'stellar' : 'normal';
    const quantityReturned = Number(result?.quantityReturned ?? listing.quantityRemaining ?? 0);
    if (quantityReturned > 0) {
      applyInventoryDelta(listing.cardId, variant, quantityReturned);
    }
    await refreshSellableCards();
    renderSellOptions();
    await refreshListings({ reset: true });
  }

  /**
   * The listing view's Buy, which is the row's Buy without the row.
   *
   * It calls the same mutation and does the same bookkeeping; what it does NOT
   * do is write into the grid's status line, because that line renders
   * underneath the dialog where nobody can read it. The dialog says it itself.
   */
  async function buyFromListingView(listing, quantity) {
    if (!MoonfallCloud?.moonExchange?.buyFromListing) {
      throw new Error(uiText('collection.exchange.offline', 'Not connected.'));
    }
    const entry = listings.find((item) => item.id === listing.id) || listing;
    const result = await trade(() => MoonfallCloud.moonExchange.buyFromListing(listing.id, quantity));
    await applyPurchaseResult(entry, quantity, result);
    return result;
  }

  async function cancelFromListingView(listing) {
    if (!MoonfallCloud?.moonExchange?.cancelListing) {
      throw new Error(uiText('collection.exchange.offline', 'Not connected.'));
    }
    const entry = listings.find((item) => item.id === listing.id) || listing;
    const result = await trade(() => MoonfallCloud.moonExchange.cancelListing(listing.id));
    await applyCancelResult(entry, result);
    return result;
  }

  async function handleBuy(listingId, qtyInput) {
    if (!MoonfallCloud?.moonExchange?.buyFromListing) return;
    const listing = listings.find((entry) => entry.id === listingId);
    if (!listing) {
      setStatus(uiText('collection.exchange.listingNotFound', 'Listing not found.'), 'error');
      return;
    }
    if (listing.isMine === true || listing.sellerId === currentUserId) {
      setStatus(uiText('collection.exchange.cannotBuyOwn', 'You cannot buy a listing you created.'), 'error');
      return;
    }

    const quantity = normalizeListingQuantity(listing, qtyInput?.value);
    if (!quantity) {
      setStatus(uiText('collection.exchange.validQuantity', 'Enter a valid quantity.'), 'error');
      return;
    }
    if (qtyInput && qtyInput.value !== String(quantity)) {
      qtyInput.value = String(quantity);
      updateListingTotal(qtyInput);
    }
    if (!canAffordListing(listing, quantity)) {
      setStatus(getNotEnoughStardustMessage(), 'error');
      syncListingAffordability(qtyInput?.closest?.('.moon-exchange-listing'));
      return;
    }

    setStatus(uiText('collection.exchange.completingPurchase', 'Completing purchase...'));
    const listingEl = qtyInput?.closest?.('.moon-exchange-listing');
    const buyBtn = listingEl?.querySelector?.('.moon-exchange-buy-btn');
    if (buyBtn) buyBtn.disabled = true;

    try {
      const result = await trade(() => MoonfallCloud.moonExchange.buyFromListing(listingId, quantity));
      await applyPurchaseResult(listing, quantity, result);

      if (result?.quantityFilled && result.quantityFilled < quantity) {
        setStatus(uiText('collection.exchange.partialPurchase', 'Purchased {{count}} prints. Listing filled.', {
          count: result.quantityFilled,
        }), 'success');
      } else {
        setStatus(uiText('collection.exchange.purchaseComplete', 'Purchase complete.'), 'success');
      }
    } catch (err) {
      if (!isNotEnoughStardustError(err)) {
        console.error('[MoonExchange] Failed to buy listing:', err);
      }
      setStatus(getPurchaseErrorMessage(err), 'error');
      await refreshListings({ reset: true });
    } finally {
      syncListingAffordability(listingEl);
    }
  }

  async function handleCancelListing(listingId, cancelBtn) {
    if (!MoonfallCloud?.moonExchange?.cancelListing) return;
    const listing = listings.find((entry) => entry.id === listingId);
    if (!listing) {
      setStatus(uiText('collection.exchange.listingNotFound', 'Listing not found.'), 'error');
      return;
    }
    if (listing.isMine !== true && listing.sellerId !== currentUserId) {
      setStatus(uiText('collection.exchange.cancelOwnOnly', 'You can only cancel listings you created.'), 'error');
      return;
    }

    setStatus(uiText('collection.exchange.cancelingListing', 'Canceling listing...'));
    if (cancelBtn) cancelBtn.disabled = true;

    try {
      const result = await trade(() => MoonfallCloud.moonExchange.cancelListing(listingId));
      await applyCancelResult(listing, result);
      setStatus(uiText('collection.exchange.canceledListing', 'Listing canceled. Prints returned.'), 'success');
    } catch (err) {
      console.error('[MoonExchange] Failed to cancel listing:', err);
      setStatus(playerFacingError(err,
        uiText('collection.exchange.cancelFailed', 'Failed to cancel listing.')), 'error');
    } finally {
      if (cancelBtn) cancelBtn.disabled = false;
    }
  }

  function guardRun(key, fn) {
    if (MoonfallCloudGuard && typeof MoonfallCloudGuard.run === 'function') {
      return MoonfallCloudGuard.run(key, fn);
    }
    return fn();
  }

  function bindGuardedClick(button, key, fn) {
    if (!button) return;
    button.addEventListener('click', () => {
      const guard = MoonfallClickGuard;
      const runner = () => guardRun(key, () => Promise.resolve(fn()));
      if (guard && typeof guard.guard === 'function') {
        guard.guard(button, runner).catch((err) => console.error('[MoonExchange] guarded click failed:', err));
      } else {
        runner().catch((err) => console.error('[MoonExchange] guarded action failed:', err));
      }
    });
  }

  function setupEventListeners() {
    const section = getSection();
    if (!section || section.dataset.exchangeListeners === 'true') return;
    section.dataset.exchangeListeners = 'true';

    window.addEventListener('pointermove', hoverIntent.observeMove, { capture: true, passive: true });

    const refreshBtn = document.getElementById('moon-exchange-refresh');
    bindGuardedClick(refreshBtn, 'moonExchange.refresh', () => refreshListings({ reset: true }));

    document.querySelectorAll('.moon-exchange-view-btn').forEach((button) => {
      button.addEventListener('click', () => {
        setMobilePane(button.dataset.pane);
        if (button.dataset.pane === 'market') scheduleLayoutSync();
      });
    });

    const filtersToggle = document.getElementById('moon-exchange-filters-toggle');
    filtersToggle?.addEventListener('click', () => {
      setFiltersOpen(filtersToggle.getAttribute('aria-expanded') !== 'true');
    });
    section.addEventListener('click', (event) => {
      const controls = event.target?.closest?.('.moon-exchange-market-controls');
      if (!controls) setFiltersOpen(false);
    });
    section.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      setFiltersOpen(false);
      filtersToggle?.focus?.();
    });

    const sellRetry = document.getElementById('moon-exchange-sell-retry');
    sellRetry?.addEventListener('click', () => refreshSellableCards({ forceRemote: true }));

    const searchInput = document.getElementById('moon-exchange-search');
    searchInput?.addEventListener('input', (event) => {
      if (searchTimer) clearTimeout(searchTimer);
      searchTimer = setTimeout(() => {
        handleSearchChange(event.target?.value || '');
      }, 150);
    });

    const sortSelect = document.getElementById('moon-exchange-sort');
    if (sortSelect) {
      sortSelect.value = currentSort;
      sortSelect.addEventListener('change', (event) => {
        handleSortChange(event.target?.value || 'date_desc');
        setFiltersOpen(false);
      });
    }

    const ownerFilterSelect = document.getElementById('moon-exchange-owner-filter');
    if (ownerFilterSelect) {
      ownerFilterSelect.value = currentOwnerFilter;
      ownerFilterSelect.addEventListener('change', (event) => {
        handleOwnerFilterChange(event.target?.value || DEFAULT_OWNER_FILTER);
        setFiltersOpen(false);
      });
    }

    const prevBtn = document.getElementById('moon-exchange-prev');
    bindGuardedClick(prevBtn, 'moonExchange.pagePrev', handlePrevPage);

    const nextBtn = document.getElementById('moon-exchange-next');
    bindGuardedClick(nextBtn, 'moonExchange.pageNext', handleNextPage);

    const select = document.getElementById('moon-exchange-card-select');
    select?.addEventListener('change', () => {
      setStatus('');
      updateAvailability();
    });

    const sellSearch = /** @type {HTMLInputElement|null} */ (document.getElementById('moon-exchange-card-search'));
    sellSearch?.addEventListener('input', () => {
      sellSearchTerm = String(sellSearch.value || '').trim();
      clearTimeout(sellSearchTimer);
      sellSearchTimer = setTimeout(() => {
        sellSearchTimer = null;
        void searchSellableCards(sellSearchTerm);
      }, 250);
    });

    const qtyInput = document.getElementById('moon-exchange-quantity');
    qtyInput?.addEventListener('input', () => {
      const max = Number(qtyInput.max || 0);
      let qty = parsePositiveInt(qtyInput.value) || 1;
      if (max && qty > max) qty = max;
      qtyInput.value = String(qty);
      updateSellerEstimate();
    });

    const priceInput = document.getElementById('moon-exchange-unit-price');
    priceInput?.addEventListener('input', () => {
      unitPriceEdited = true;
      updateSellerEstimate();
    });

    const createBtn = document.getElementById('moon-exchange-create-btn');
    bindGuardedClick(createBtn, 'moonExchange.createListing', handleCreateListing);

    const listingsContainer = document.getElementById('moon-exchange-listings');
    listingsContainer?.addEventListener('input', (event) => {
      if (event.target?.classList?.contains('moon-exchange-buy-qty')) {
        updateListingTotal(event.target);
      }
    });

    listingsContainer?.addEventListener('click', (event) => {
      const buyBtn = event.target.closest('.moon-exchange-buy-btn');
      if (buyBtn) {
        const listingEl = buyBtn.closest('.moon-exchange-listing');
        const qtyField = listingEl?.querySelector('.moon-exchange-buy-qty');
        const listingId = listingEl?.dataset?.listingId;
        if (!listingId || !qtyField) return;
        const guard = MoonfallClickGuard;
        const runner = () => guardRun(`moonExchange.buyFromListing:${listingId}`, () => Promise.resolve(handleBuy(listingId, qtyField)));
        if (guard && typeof guard.guard === 'function') {
          guard.guard(buyBtn, runner, { cooldownMs: 500 }).catch((err) => console.error('[MoonExchange] buy guard failed:', err));
        } else {
          runner().catch((err) => console.error('[MoonExchange] buy failed:', err));
        }
        return;
      }

      const cancelBtn = event.target.closest('.moon-exchange-cancel-btn');
      if (!cancelBtn) return;
      const listingEl = cancelBtn.closest('.moon-exchange-listing');
      const listingId = listingEl?.dataset?.listingId;
      if (!listingId) return;
      const guard = MoonfallClickGuard;
      const runner = () => guardRun(`moonExchange.cancelListing:${listingId}`, () => Promise.resolve(handleCancelListing(listingId, cancelBtn)));
      if (guard && typeof guard.guard === 'function') {
        guard.guard(cancelBtn, runner, { cooldownMs: 500 }).catch((err) => console.error('[MoonExchange] cancel guard failed:', err));
      } else {
        runner().catch((err) => console.error('[MoonExchange] cancel failed:', err));
      }
    });
  }

  function syncSortSelect() {
    const sortSelect = document.getElementById('moon-exchange-sort');
    if (sortSelect) {
      sortSelect.value = currentSort;
    }
  }

  function syncOwnerFilterSelect() {
    const ownerFilterSelect = document.getElementById('moon-exchange-owner-filter');
    if (ownerFilterSelect) {
      ownerFilterSelect.value = currentOwnerFilter;
    }
  }

  /* A LANGUAGE CHANGE RE-DESCRIBES THE ROWS (round 18b, 29 September 2026).
     A row's words are its card's, in the reader's language: the page's own
     cards are kept per language, and the Collection keeps its catalog across
     a language change. The rows were re-rendered at once with what was in
     hand — the catalog's OLD words, or, with no card in the new language,
     the listing's English snapshot. The rows the lane shows have their cards
     read in the new language first (the details route); the rows keep their
     words until those land (capped), then change in one step. */
  function readShownRowsCards() {
    return readEntryCards({ listings }, { forFaces: true });
  }

  function rerenderLocalizedExchangeUi() {
    localeRevision += 1;
    const revision = localeRevision;
    applyExchangeTranslations();
    renderSellOptions();
    updatePaginationControls();
    updateSellerEstimate();
    const shownHaveWords = !listingsMissFaces(listings.slice(0, activePageSize));
    const cardsRead = shownHaveWords
      ? Promise.resolve()
      : Promise.race([
        readShownRowsCards(),
        new Promise((resolve) => {
          setTimeout(resolve, EXCHANGE_REVEAL_CAP_MS);
        }),
      ]);
    void cardsRead.then(() => {
      // A newer change re-describes them itself.
      if (revision === localeRevision) void renderListings();
    });
  }

  function setupI18nListeners() {
    if (i18nListenersReady) return;
    i18nListenersReady = true;
    window.addEventListener('moonfall:i18nready', rerenderLocalizedExchangeUi);
    window.addEventListener('moonfall:languagechange', rerenderLocalizedExchangeUi);
    window.addEventListener('moonfall:viewport-change', handleViewportChange);
    // Passive: these only observe, they never prevent the gesture.
    window.addEventListener('touchstart', beginTouchGesture, { passive: true });
    window.addEventListener('touchend', endTouchGesture, { passive: true });
    window.addEventListener('touchcancel', endTouchGesture, { passive: true });
    // Intent on the Collection's Moon Exchange tab — a pointer over it,
    // keyboard focus, the first touch — is the one warm that reads the owned
    // catalog and the entry art (warm()); the Collection's open warms only
    // the config and the entry page. Delegated: the tab is the Collection's
    // markup.
    const warmOnIntent = (event) => {
      if (event.target?.closest?.('#collection-tab-exchange')) void warm({ intent: true });
    };
    document.addEventListener('pointerover', warmOnIntent, { passive: true });
    document.addEventListener('focusin', warmOnIntent);
    document.addEventListener('touchstart', warmOnIntent, { passive: true });
  }

  /**
   * @param {object} [context]
   * @param {Array<any>} [context.allCards]
   * @param {Map<string, number>} [context.ownedCardMap]
   * @param {Map<string, number>} [context.ownedCardStellarMap]
   * @param {string|null} [context.currentUserId]
   * @param {number} [context.stardust]
   * @param {Function} [context.onStardustUpdate]
   * @param {Function} [context.onInventoryUpdate]
   * @param {(() => Promise<unknown[]>)|null} [context.loadCards]
   * @param {boolean} [context.catalogPending] the tab opened before the
   *   owned catalog, which is on its way
   * @param {boolean} [context.catalogFailed] that catalog failed
   * @param {boolean} [context.catalogComplete] false: the collection is too
   *   large to list whole, and the sell column searches instead
   */
  function setContext({
    allCards: nextCards = [],
    ownedCardMap: nextOwnedMap,
    ownedCardStellarMap: nextOwnedStellarMap,
    currentUserId: nextUserId,
    stardust: nextStardust,
    onStardustUpdate: nextStardustUpdate,
    onInventoryUpdate: nextInventoryUpdate,
    // The owned catalog's loader, kept for intent on the tab (warm() takes it
    // too); called here only for an intent that came before it.
    loadCards: nextLoadCards,
    // The tab opened before the catalog, which is on its way: `allCards` is
    // whatever the Collection holds meanwhile, and the sell column waits.
    catalogPending: nextCatalogPending = false,
    // That catalog failed: the sell column says so (the market still works).
    catalogFailed: nextCatalogFailed = false,
    // The player's collection is too large to list whole: the column searches.
    catalogComplete: nextCatalogComplete = true,
  } = {}) {
    if (typeof nextLoadCards === 'function') {
      cardsLoader = nextLoadCards;
      if (catalogIntentPending && !isOpen) void warm();
    }
    listingsRenderGeneration += 1;
    sellPreviewGeneration += 1;
    document.querySelector('#moon-exchange-listings > .moon-exchange-paint-stage')?.remove();
    // A catalog read anew is in the reader's language; the same one handed
    // back after a language change is not.
    if (Array.isArray(nextCards) && nextCards.length > 0 && nextCards !== allCards) {
      catalogLanguage = readerLanguage();
    }
    allCards = Array.isArray(nextCards) ? nextCards : [];
    ownedCardMap = nextOwnedMap instanceof Map ? nextOwnedMap : new Map();
    ownedCardStellarMap = nextOwnedStellarMap instanceof Map ? nextOwnedStellarMap : new Map();
    currentUserId = nextUserId || null;
    stardust = Number.isFinite(nextStardust) ? nextStardust : stardust;
    onStardustUpdate = nextStardustUpdate || onStardustUpdate;
    onInventoryUpdate = nextInventoryUpdate || onInventoryUpdate;
    const catalogWasPending = catalogPending;
    catalogPending = nextCatalogPending === true;
    const searchWasNeeded = !catalogComplete;
    catalogComplete = nextCatalogComplete !== false;
    syncSellSearchBox();
    if (catalogPending) {
      // The sell column's list is the catalog: until it lands, the column
      // shows the card it last showed (THE SELL COLUMN'S LAST CARD), or waits.
      sellableCards = rememberedSellCards();
      sellListSource = sellableCards.length > 0 ? 'record' : null;
    } else if (nextCatalogFailed === true) {
      sellableCards = [];
      sellListSource = null;
      sellableLoadError = uiText('collection.exchange.loadSellableFailed', 'Sell inventory is unavailable.');
      sellErrorFromCatalog = true;
    } else {
      if (sellErrorFromCatalog && allCards.length > 0) {
        sellableLoadError = null;
        sellErrorFromCatalog = false;
      }
      syncSellableCardsFromCatalog();
    }
    if (!catalogPending) {
      catalogLanded?.resolve();
      catalogLanded = null;
    }
    // The catalog has landed on an open tab: its sell list is read as an
    // entry with the catalog in hand reads it (from the catalog, or — with
    // no card or holding in it — from the server).
    if (catalogWasPending && !catalogPending && nextCatalogFailed !== true && isOpen) {
      void refreshSellableCards();
    } else if (!catalogComplete && !searchWasNeeded && isOpen) {
      // The collection grew past what is listed whole while the tab was open.
      void searchSellableCards('');
    }

    const hasSkeletons = document.querySelector('.moon-exchange-skeleton');
    // The open sell form receives its catalog independently of the browse
    // lane. Loading listings must not leave localized sell names stale.
    if (isOpen || !hasSkeletons) renderSellOptions();
    if (!hasSkeletons) {
      renderListings();
      updatePaginationControls();
    }
  }

  /* THE PRESS IS ANSWERED BEFORE THE ENTRY IS BUILT (round 18, 28 September
     2026). The entry's rows and the sell column's card are built behind the
     hold, and a page in hand (the warm's, or a kept one) resolves at once —
     so they were built in the press's own task, before the frame that shows
     the press: 40-53 ms of it on the WebKit phone offline (its layout reads
     and raster paints; building the card elements is 1-2 ms), where the tab
     answered in 8-16 ms while the rows still waited for the catalog. The
     tab's pressed and active looks are transitions, whose first frame is
     their start value, so a press shows on the second frame after it; on
     the WebKit 4G seat that frame came after the rows' rasters, and the
     meter read the press at 303-386 ms (decks17e). The entry now waits for
     the press's first two frames (~33 ms at 60 Hz, capped for a page that
     draws no frames), whatever it costs after. */
  const PRESS_FRAMES_CAP_MS = 100;

  function pressAnswered() {
    if (typeof requestAnimationFrame !== 'function') return Promise.resolve();
    return new Promise((resolve) => {
      const cap = setTimeout(resolve, PRESS_FRAMES_CAP_MS);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        // A task after the second frame's paint, not a callback before it.
        setTimeout(() => {
          clearTimeout(cap);
          resolve(undefined);
        }, 0);
      }));
    });
  }

  /**
   * THE TAB APPEARS IN ONE FRAME. prepare() has held the section; the reads
   * run together — config, search backfill, first page — and the section is
   * revealed in the task that commits that page (capped once it has answered).
   * A remembered first page (the warm, or the last visit) is presented at
   * once. Within LISTINGS_WARM_FRESH_MS it is the entry's answer; a sliced
   * one, or one read before this player's last trade, is re-read behind the
   * view, and the reconciling render changes nothing unless the answer did.
   */
  async function open() {
    isOpen = true;
    const thisOpen = ++openGeneration;
    // A page read after the press answered this entry (open() alone: now).
    const pressedAt = entryPressedAt || Date.now();
    entryPressedAt = 0;
    catalogIntentPending = false;
    // The Collection hands the catalog over (setContext) just before open(),
    // or says it is on its way (catalogPending).
    if (allCards.length > 0 && !catalogPending) catalogInHand = true;
    beginEntryHold();
    currentSort = loadStoredSort();
    currentOwnerFilter = DEFAULT_OWNER_FILTER;
    ensureLayout();
    syncCommandBarPlacement();
    setupEventListeners();
    syncSortSelect();
    syncOwnerFilterSelect();
    const requestedPane = document.querySelector('.moon-exchange-grid')?.dataset?.mobilePane;
    setMobilePane(requestedPane === 'sell' ? 'sell' : 'market');
    observeListingLayout();
    syncListingsPageSize();
    viewportRenderSignature = getViewportRenderSignature();
    searchText = '';
    const searchInput = document.getElementById('moon-exchange-search');
    if (searchInput) searchInput.value = '';
    searchBackfillStatus = null;
    clearSearchBackfillPoll();

    let presentedFromMemory = null;
    try {
      // The rows print the buy lock, so they wait for the config's answer —
      // but only when there is no fresh one to print; a fresh answer is
      // re-read behind the view, and a changed lock re-renders the rows.
      const cachedConfig = configCache && Date.now() - configCache.at <= CONFIG_FRESH_MS
        ? configCache.config
        : null;
      let configRead;
      if (cachedConfig) {
        applyConfig(cachedConfig);
        configRead = Promise.resolve();
        const lockBefore = JSON.stringify(sellUnlock);
        void ensureConfig().then(() => {
          if (isOpen && JSON.stringify(sellUnlock) !== lockBefore) void renderListings();
        });
      } else {
        configRead = ensureConfig();
      }
      void ensureSearchTextBackfill();
      // The reads are on their way; the build waits for the press's frames.
      await pressAnswered();
      if (!isOpen || thisOpen !== openGeneration) return;
      // A collection too large to list whole finds its sell cards by search,
      // which may wait on its index: the tab never waits for that column.
      const sellableRead = catalogComplete ? refreshSellableCards() : (void refreshSellableCards(), null);

      const remembered = await joinRememberedEntryPage(entryRequest(activePageSize));
      if (remembered && isOpen) {
        presentedFromMemory = remembered;
        await configRead;
        currentCursor = null;
        cursorStack = [];
        listings = remembered.listings;
        nextCursor = remembered.nextCursor;
        isDone = remembered.isDone;
        listingsLoadError = null;
        const listingSnapshot = listings;
        loadListingStellarAssets(listingSnapshot).then((assetsLoaded) => {
          if (!assetsLoaded || !isOpen || isStaleInstance() || listings !== listingSnapshot) return;
          hydrateListingStellarPreviews(listingSnapshot);
        });
        entryHold?.armCap(EXCHANGE_REVEAL_CAP_MS);
        const committed = await renderListings();
        updatePaginationControls();
        if (committed && isOpen) readPageAhead();
      } else {
        await refreshListings({
          reset: true,
          beforeRender: configRead,
          onData: () => entryHold?.armCap(EXCHANGE_REVEAL_CAP_MS),
        });
      }
      if (sellableRead) await sellableRead;
      // A render another caller superseded (a context sync mid-build) is not
      // a finished lane: build it before the section is revealed.
      if (isOpen && !laneHasCommittedContent()) await renderListings();
      // The sell column's card list is the catalog: with that column on
      // screen and no card of its own to show (THE SELL COLUMN'S LAST CARD),
      // the first frame waits for it (capped); a phone's market pane does not.
      if (isOpen && catalogPending && sellColumnShown() && sellableCards.length === 0) {
        await catalogLandedWithin(EXCHANGE_REVEAL_CAP_MS);
      }
      if (isOpen) await sellPreviewSettled();
    } finally {
      // Not the hold of a newer open that overtook this one.
      if (isOpen && thisOpen === openGeneration) releaseEntryHold();
    }
    // A page presented from memory is read again behind the view (a sliced
    // one learns its real cursor there, one read before this player's last
    // trade its outcome), unless its own read answered after the press.
    if (
      isOpen
      && thisOpen === openGeneration
      && presentedFromMemory
      && !(presentedFromMemory.startedAt >= pressedAt)
    ) {
      revalidateShownPage(pressedAt);
    }

    /* THE SELLER'S ONLY IN-SESSION CHANCE.
       `buyFromListing` grants the Exchange Ledger to both sides, but only the
       BUYER holds that response — the seller is not at the keyboard when their
       card sells, and there is no push channel to reach them. So their copy had
       no delivery at all: they could sell all week with the game open and never
       be told, until the next time they launched.

       Opening the Exchange is where they come looking, so it is the honest
       earliest moment. On a deliberate user action — not per listing, per page
       or per refresh — and rate-limited so that reopening the tab twenty times
       costs one call, not twenty. A sale that lands inside that window is not
       lost: the next open, or the next launch, still drains it. */
    const now = Date.now();
    if (now - lastUnlockDrainAt >= UNLOCK_DRAIN_INTERVAL_MS) {
      lastUnlockDrainAt = now;
      void getCloudAchievementBannerConsumer()?.();
    }
  }

  function close() {
    isOpen = false;
    // The Collection closes this surface with itself: an intent it never
    // handed a loader to is not carried into its next open.
    catalogIntentPending = false;
    // Nor are the rows' pictures read ahead for it: the player moved on.
    dropRowsArt();
    listingsRenderGeneration += 1;
    sellPreviewGeneration += 1;
    // The generation bump alone only retires the observer the next time it
    // fires, which on a closed surface may be never — so it would sit holding
    // the card and its slot alive. Drop it here.
    sellPreviewFitObserver?.disconnect();
    sellPreviewFitObserver = null;
    // A sell search still being typed is not sent after the tab closed.
    clearTimeout(sellSearchTimer);
    sellSearchTimer = null;
    releaseEntryHold('close');
    const listingsRoot = document.getElementById('moon-exchange-listings');
    // Loads that never finished end here, so no hidden retry outlives the
    // tab; rows whose art settled keep it, and are exactly what the next
    // visit presents. A row that lost its art is rebuilt by that visit.
    if (disposeUnsettledExchangeImages(listingsRoot) && listingsRoot) {
      delete listingsRoot.dataset.renderSignature;
    }
    listingsRoot?.querySelector(':scope > .moon-exchange-paint-stage')?.remove();
    stopObservingListingLayout();
    viewportRenderSignature = '';
    setFiltersOpen(false);
    setStatus('');
    if (searchTimer) {
      clearTimeout(searchTimer);
      searchTimer = null;
    }
    clearSearchBackfillPoll();
  }

  function init() {
    currentSort = loadStoredSort();
    ensureLayout();
    syncCommandBarPlacement();
    setupEventListeners();
    setupI18nListeners();
  }

  return {
    init,
    open,
    close,
    prepare,
    warm,
    warmConfig,
    setContext,
    refreshInventory,
    prefillSellForm,
    showLoadingSkeleton,
    isOpen: () => isOpen,
    _instanceToken: instanceToken,
  };
})();


// The four helpers below were previously reachable ONLY through a
// `module.exports` block written for the classic-script era. Removing that
// block (correctly — it clobbered the real ESM export under the Jest
// transform) took their only export path with it. They are real named exports
// now, which is what they should always have been; the bundle is unaffected
// because esbuild drops what the app never imports.
export {
  MoonExchange,
  calculateListingPageSize,
  calculatePreviewCardScale,
  fitCardInsidePreview,
  celebrateExchangeStatus,
};
